/* Import components */

import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { axios } from '../libs/axios';
import { buildInvoicePaymentLink } from '../libs/paymentPortal';
import { DataTablePaginated } from '../components';
import { InlineSvgIcon } from '../components/InlineSvgIcon';
import { User } from '../states/actions';
import logger from '../utils/logger';
/* REPLY SOLUTION: to be removed from code in future.
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from 'ckeditor5-custom-build';
import { ckEditorReply } from '../config';
*/
import { GlobalStateContext } from '../components/GlobalState';
import LawyerProjectConversation from '../components/LawyerProjectConversation';
import LawyerProjectThreadPanel from '../components/LawyerProjectThreadPanel';
import { LegalFieldPicker, LegalFieldAiClassify } from '../components/Project/LegalFieldPicker';
import { ProjectTypeInfoTooltip } from '../components/Project/ProjectTypeInfoTooltip';
import { ProjectDocumentsList } from '../components/Project/ProjectDocumentsList';
import { ClientEditModal } from '../components/Client/ClientEditModal';
import { useTranslation } from '../i18n/I18nContext';
import { getPrepaymentAvailable, getPrepaymentReceived } from '../utils/prepaymentBalance';
import {
	canEditInvoiceLine,
	getInvoiceEditWindowEnd,
} from '../utils/invoiceEditWindow';

/* Format date */

function formatDateDMY(date) {
	if (!date) return '';
	const d = new Date(date);
	const day = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year = d.getFullYear();
	return `${day}.${month}.${year}`;
}

function formatDateDMYTime(date) {
	if (!date) return '';
	const d = new Date(date);
	const day = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year = d.getFullYear();
	const hours = d.getHours().toString().padStart(2, '0');
	const minutes = d.getMinutes().toString().padStart(2, '0');
	return `${day}.${month}.${year} ${hours}:${minutes}`;
}

function formatSidebarDate(date) {
	if (!date) return '';
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return '';
	const day = d.getDate().toString().padStart(2, '0');
	const month = (d.getMonth() + 1).toString().padStart(2, '0');
	const year = d.getFullYear();
	return `${day}.${month}.${year}`;
}

function formatSidebarMoney(amount) {
	const n = Number(amount) || 0;
	const rounded = Math.round(n * 100) / 100;
	if (rounded === 0) return '0€';
	if (rounded % 1 === 0) return `${rounded.toFixed(0)}€`;
	return `${rounded.toFixed(2)}€`;
}

function formatSidebarDuration(minutes) {
	const m = Math.round(Number(minutes) || 0);
	const hours = Math.floor(m / 60);
	const mins = m % 60;
	return `${hours}:${mins.toString().padStart(2, '0')}`;
}

function formatSidebarHourlyRate(rate) {
	const n = Number(rate) || 0;
	const formatted = n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
	return `${formatted}€/h`;
}

const PROJECT_TABLE_MIN_WIDTH = 1060;
const PROJECT_SIDEBAR_WIDTH = 320;
const PROJECT_INFO_GAP = 24;
const PROJECT_SIDEBAR_PINNED_MIN_WIDTH =
	PROJECT_TABLE_MIN_WIDTH + PROJECT_INFO_GAP + PROJECT_SIDEBAR_WIDTH;

function computeInvoiceSum(invoiceList) {
	return (invoiceList || [])
		.filter((inv) => inv.type === 'invoice' && inv.status !== 'credited')
		.reduce((sum, inv) => sum + (parseFloat(inv.amount_total) || 0), 0);
}

function getLawyerInitials(firstName, lastName) {
	const first = (firstName || '').trim().charAt(0);
	const last = (lastName || '').trim().charAt(0);
	return `${first}${last}`.toUpperCase() || '?';
}

function formatLawyerRating(rating) {
	const value = Number(rating);
	if (Number.isNaN(value)) return '0.0';
	return value.toFixed(1);
}

function applyProjectTaskPatchResponse(prev, response) {
	if (!response || typeof response !== 'object') {
		return prev;
	}
	return {
		...prev,
		related_tasks: response.related_tasks ?? prev.related_tasks,
		deadline_earliest: Object.prototype.hasOwnProperty.call(response, 'deadline_earliest')
			? response.deadline_earliest
			: prev.deadline_earliest,
		total_billed_minutes: response.total_billed_minutes ?? prev.total_billed_minutes,
		total_unbilled_minutes: response.total_unbilled_minutes ?? prev.total_unbilled_minutes,
	};
}

/* Convert line breaks to HTML */

function convertLineBreaksToHtml(text) {
	if (!text) return '';
	return text.replace(/\n/g, '<br>');
}

/* Strip HTML tags from text */
function stripHtmlTags(html) {
	if (!html) return '';
	const tmp = document.createElement('DIV');
	tmp.innerHTML = html;
	return tmp.textContent || tmp.innerText || '';
}

/* Export project */

const ProjectComponent = () => {
	const {projectNo} = useParams();
	const location = useLocation();
	const history = useHistory();
	const { globalState } = useContext(GlobalStateContext);
	const [project, setProject] = useState(null);
	const [loading, setLoading] = useState(false); // Start with false since access is checked in wrapper
	const [minLoadingTime, setMinLoadingTime] = useState(false);
	const [accessError, setAccessError] = useState(null); // Error message when access is denied
	const currentUser = useSelector(User.get());
	const { t, locale } = useTranslation();
	const lastProjectNoRef = useRef(null);
	const lastLocationKeyRef = useRef(null);
	const justClaimedRef = useRef(false); // Flag to indicate we just claimed this prospect
	const retryCountRef = useRef(0); // Track retries to prevent infinite loop
	const lawyerProjectThreadRef = useRef(null);
	const [lastThreadMessageFromClient, setLastThreadMessageFromClient] = useState(false);
	const [clientPanelOpen, setClientPanelOpen] = useState(false);
	const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= 768);
	const [isSidebarPinned, setIsSidebarPinned] = useState(false);
	const [sidebarDocsExpanded, setSidebarDocsExpanded] = useState(false);
	const pageContainerRef = useRef(null);
	const handleLawyerThreadHeadChange = useCallback(({ lastMessageFromClient }) => {
		setLastThreadMessageFromClient(!!lastMessageFromClient);
	}, []);

	useEffect(() => {
		setLastThreadMessageFromClient(false);
	}, [project?._id]);

	useEffect(() => {
		setClientPanelOpen(false);
		setShowClientEditModal(false);
		setSidebarDocsExpanded(false);
	}, [project?._id]);

	useEffect(() => {
		document.body.classList.toggle('mobile-client-panel-open', isMobileView && clientPanelOpen);
		return () => document.body.classList.remove('mobile-client-panel-open');
	}, [isMobileView, clientPanelOpen]);

	useEffect(() => {
		const onResize = () => {
			const mobile = window.innerWidth <= 768;
			setIsMobileView(mobile);
			if (!mobile) {
				setClientPanelOpen(false);
			}
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	useEffect(() => {
		if (!project || loading || minLoadingTime) return undefined;

		const pageContainer = pageContainerRef.current;
		if (!pageContainer) return undefined;

		const updateSidebarPinned = () => {
			const width = pageContainer.getBoundingClientRect().width;
			setIsSidebarPinned(
				width >= PROJECT_SIDEBAR_PINNED_MIN_WIDTH && window.innerWidth > 768
			);

			const xContent = pageContainer.closest('.x_content');
			const pageContent = pageContainer.querySelector('.page-content');
			if (xContent && pageContent) {
				const offset = Math.max(
					20,
					Math.round(
						pageContent.getBoundingClientRect().top
							- xContent.getBoundingClientRect().top
							+ xContent.scrollTop
					)
				);
				pageContainer.style.setProperty('--project-sidebar-rest-offset', `${offset}px`);
			}
		};

		updateSidebarPinned();
		const resizeObserver = new ResizeObserver(updateSidebarPinned);
		resizeObserver.observe(pageContainer);
		window.addEventListener('resize', updateSidebarPinned);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateSidebarPinned);
		};
	}, [project, loading, minLoadingTime]);

	// Owner – fetched user object (includes partner_vat Boolean and partner_hourly_rate)
	
	const [owner, setOwner] = useState(null);
	
	// Client – fetched client object for current client data
	const [client, setClient] = useState(null);
	
	// Owner change modal and data
	const [showChangeOwnerModal, setShowChangeOwnerModal] = useState(false);
	const [matchLawyers, setMatchLawyers] = useState([]);
	const [matchCriteria, setMatchCriteria] = useState(null);
	const [selectedNewOwnerId, setSelectedNewOwnerId] = useState('');
	const [ownerChangeLoading, setOwnerChangeLoading] = useState(false);
	/* REPLY SOLUTION: to be removed from code in future.
	const [clearInitialResponse, setClearInitialResponse] = useState(false);
	const [restoreReplyTaskIndex, setRestoreReplyTaskIndex] = useState(null);
	*/
	const [ownerChangeFeedback, setOwnerChangeFeedback] = useState([]);
	const [reassignHourlyRate, setReassignHourlyRate] = useState('');
	
	// Invoices
	
	const [prepayments, setPrepayments] = useState([]);
	const [invoices, setInvoices] = useState([]);
	const [creditInvoices, setCreditInvoices] = useState([]);

	// Other modals remain unchanged…

	const [showPrepaymentModal, setShowPrepaymentModal] = useState(false);
	const [showGenerateInvoiceModal, setShowGenerateInvoiceModal] = useState(false);
	const [showInvoiceConfirmation, setShowInvoiceConfirmation] = useState(false);
	const [showFinishProjectModal, setShowFinishProjectModal] = useState(false);
	const [showCommentModal, setShowCommentModal] = useState(false);
	
	// Custom hourly rate modal
	const [showHourlyRateModal, setShowHourlyRateModal] = useState(false);
	const [customHourlyRate, setCustomHourlyRate] = useState('');
	const [hourlyRateLoading, setHourlyRateLoading] = useState(false);
	
	/* Unified Add/Edit Task Modal State */
	
	const [showTaskModal, setShowTaskModal] = useState(false);
	const [isEditingTask, setIsEditingTask] = useState(false);
	const [isInvoiceLineEdit, setIsInvoiceLineEdit] = useState(false);
	const [taskModalData, setTaskModalData] = useState(null);
	const [taskModalErrors, setTaskModalErrors] = useState({});
	const [taskDetails, setTaskDetails] = useState({});
	
	// Comment state
	
	const [commentText, setCommentText] = useState('');
	const [commentExpanded, setCommentExpanded] = useState(false);
	
	// Add Prepayment form – using hours and minutes dropdowns
	
	const [prepaymentHours, setPrepaymentHours] = useState(0);
	const [prepaymentMinutes, setPrepaymentMinutes] = useState(0);
	const [prepaymentComment, setPrepaymentComment] = useState('');
	const [prepaymentDueDays, setPrepaymentDueDays] = useState(7);
	const [showPrepaymentConfirmation, setShowPrepaymentConfirmation] = useState(false);
	const [prepaymentFeedback, setPrepaymentFeedback] = useState('');
	const [isPrepaymentProcessing, setIsPrepaymentProcessing] = useState(false);
	
	// Generate Invoice info and processing states
	
	const [unbilledTasksTotal, setUnbilledTasksTotal] = useState(0);
	const [paidPrepaymentsTotal, setPaidPrepaymentsTotal] = useState(0);
	const [previouslyBilled, setPreviouslyBilled] = useState(0);
	const [prepaymentAmountBilled, setPrepaymentAmountBilled] = useState(0);
	const [finalInvoiceAmount, setFinalInvoiceAmount] = useState(0);
	const [invoiceDueDays, setInvoiceDueDays] = useState(7);
	const [invoiceFeedback, setInvoiceFeedback] = useState('');
	const [isInvoiceProcessing, setIsInvoiceProcessing] = useState(false);
	const [sendEmail, setSendEmail] = useState(false);
	
	// Tooltip for finishing task without date
	
	const [finishTooltip, setFinishTooltip] = useState({ visible: false, x: 0, y: 0, message: "" });
	
	// Tooltip for disabled button clicks
	
	const [clickTooltip, setClickTooltip] = useState({ visible: false, x: 0, y: 0, message: "" });
	
	// Tooltip for hover (follows mouse cursor)
	
	const [hoverTooltip, setHoverTooltip] = useState({ visible: false, x: 0, y: 0, message: "" });
	
	// Accordion logic for Description
	
	const [descExpanded, setDescExpanded] = useState(false);
	
	const [showActionMenu, setShowActionMenu] = useState(false);
	const toggleActionMenu = () => setShowActionMenu(open => !open);
	
	// Edit project modal state
	const [showEditProjectModal, setShowEditProjectModal] = useState(false);
	const [editProjectForm, setEditProjectForm] = useState({
		project_name: '',
		project_description: '',
		project_type: ''
	});
	const [classifyingFields, setClassifyingFields] = useState(false);
	const [fieldProposal, setFieldProposal] = useState(null);
	const [classifyError, setClassifyError] = useState(null);
	// Cancel project modal state
	const [showCancelProjectModal, setShowCancelProjectModal] = useState(false);
	const [cancelReason, setCancelReason] = useState('');
	const [cancelReasonError, setCancelReasonError] = useState('');
	const [isCancelling, setIsCancelling] = useState(false);
	
	/* REPLY SOLUTION: to be removed from code in future.
	// Initial response state
	const [showInitialResponseModal, setShowInitialResponseModal] = useState(false);
	const [showSendConfirmationModal, setShowSendConfirmationModal] = useState(false);
	const [initialResponseMessage, setInitialResponseMessage] = useState('');
	const [initialResponseSubject, setInitialResponseSubject] = useState('Hugo Legal vastus');
	const [isSendingInitialResponse, setIsSendingInitialResponse] = useState(false);
	const [isSavingDraft, setIsSavingDraft] = useState(false);
	const [initialResponseExpanded, setInitialResponseExpanded] = useState(false);
	const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
	const [fromEmailForConfirmation, setFromEmailForConfirmation] = useState('');
	const [editorExpanded, setEditorExpanded] = useState(true); // Start with sidebar collapsed (closed)
	*/
	const [showClientEditModal, setShowClientEditModal] = useState(false);
	
	// File upload state
	const [isDraggingOver, setIsDraggingOver] = useState(false);
	const [uploadError, setUploadError] = useState('');
	
	/* Helper Functions */
	
	// Get effective hourly rate (priority: custom rate > project_price > owner's default rate)
	const getEffectiveHourlyRate = () => {
		// 1. Custom hourly rate (overrides everything)
		if (project.project_hourly_rate !== null && project.project_hourly_rate !== undefined && project.project_hourly_rate > 0) {
			return Number(project.project_hourly_rate);
		}
		// 2. Project price (locked at project creation)
		if (project.project_price !== null && project.project_price !== undefined && project.project_price > 0) {
			return Number(project.project_price);
		}
		// 3. Owner's default rate (fallback)
		return Number(owner?.partner_hourly_rate || 0);
	};
	
	// Check if project can be cancelled (no invoices, prepayments, or credit invoices)
	const canCancelProject = () => {
		// Cancel project is always available for all users
		return true;
	};
	
	// Helper functions for display names (still needed for business logic)
	const getClientName = () => {
		if (client) {
			return client.type === 'business' ? client.business_name : `${client.firstname} ${client.lastname}`;
		}
		return project?.client_name || '';
	};
	
	const getRepresentativeName = () => {
		if (client) {
			return `${client.firstname} ${client.lastname}`;
		}
		return project?.client_name || '';
	};

	const getClientType = () => {
		if (client?.type) {
			return client.type;
		}
		return project?.client_type || 'person';
	};

	const isBusinessClient = () => getClientType() === 'business';

	const refreshClientAndProject = useCallback(async () => {
		if (!project?.related_client_id) return null;
		try {
			const requests = [
				axios.get(`/clients/${project.related_client_id}`),
			];
			if (project?.project_no) {
				requests.push(axios.get(`/projects/${project.project_no}`));
			}
			const [clientData, projectResponse] = await Promise.all(requests);
			setClient(clientData);
			if (projectResponse) {
				setProject(projectResponse);
			}
			return clientData;
		} catch (error) {
			logger.error('Failed to refresh client and project:', error);
			return null;
		}
	}, [project?.related_client_id, project?.project_no]);

	const openClientViewModal = useCallback(async () => {
		if (!project?.related_client_id) return;
		try {
			const clientData = await axios.get(`/clients/${project.related_client_id}`);
			setClient(clientData);
			setShowClientEditModal(true);
		} catch (error) {
			logger.error('Failed to fetch client for view modal:', error);
		}
	}, [project?.related_client_id]);

	const handleAdminUnlockField = async (field) => {
		if (!project?.related_client_id || !owner?._id) return;
		try {
			await axios.post(`/clients/${project.related_client_id}/admin-unlock-field`, {
				field,
				lawyerId: owner._id,
				projectId: project._id,
			});
			await refreshClientAndProject();
			setShowActionMenu(false);
			window.nSuccess({ title: t('common.successTitle'), text: t('project.adminUnlockSuccess'), icon: 'fa-check-square' });
		} catch (error) {
			logger.error('Admin unlock failed:', error);
			window.nError({ title: t('common.errorTitle'), text: t('project.adminUnlockError'), icon: 'fa-exclamation-triangle' });
		}
	};
	
	// Refresh project details (and thereby task list, comment etc.)
	
	const refreshProject = useCallback(async () => {
		// Clear any previous access errors when refreshing
		setAccessError(null);
		setLoading(true);
		try {
			// First fetch project to get the _id, then fetch invoices
			// Add cache-busting timestamp to ensure fresh data
			const cacheBuster = `?t=${Date.now()}`;
			logger.log('[refreshProject] Fetching project:', projectNo);
			const projectResponse = await axios.get(`/projects/${projectNo}${cacheBuster}`);
			const invoiceResponse = await axios.get(`/invoices/project/${projectResponse._id}${cacheBuster}`);
			
		// Process invoice data
		const invoicesArray = Array.isArray(invoiceResponse) ? invoiceResponse : [];
		const prepaymentInvoices = invoicesArray.filter((inv) => inv.type === 'prepayment');
		const regularInvoices = invoicesArray.filter((inv) => inv.type === 'invoice');
		const creditInvoicesData = invoicesArray.filter((inv) => inv.type === 'credit');
			
			// Prepare owner and client data fetching
			const ownerPromise = projectResponse.project_owner_id 
				? axios.get(`/user/no/${projectResponse.project_owner_id}`).catch(err => {
					logger.warn('Failed to fetch project owner:', err);
					return null;
				})
				: Promise.resolve(null);
				
			const clientPromise = projectResponse.related_client_id 
				? axios.get(`/clients/${projectResponse.related_client_id}`).catch(err => {
					logger.warn('Failed to fetch client details:', err);
					return null;
				})
				: Promise.resolve(null);
			
			// Fetch owner and client data in parallel
			const [ownerData, clientData] = await Promise.all([ownerPromise, clientPromise]);
			
			// Batch all state updates together to minimize re-renders
			// Using React's automatic batching (React 18+) or setTimeout for older versions
			setTimeout(() => {
				setProject(projectResponse);
				setPrepayments(prepaymentInvoices);
				setInvoices(regularInvoices);
				setCreditInvoices(creditInvoicesData);
				setOwner(ownerData);
				setClient(clientData);
				setLoading(false);
				
				// Clear the justClaimed flag and retry counter on successful refresh
				justClaimedRef.current = false;
				retryCountRef.current = 0;
				
				// Ensure minimum loading time to prevent flashing
				setTimeout(() => {
					setMinLoadingTime(false);
				}, 300);
			}, 0);
			
		} catch (error) {
			logger.error('[refreshProject] Error:', error);
			logger.log('[refreshProject] Error status:', error?.response?.status);
			logger.log('[refreshProject] Error data:', error?.response?.data);
			
			// Check if it's a 403 (Access Denied) error
			if (error?.response?.status === 403) {
				logger.log('[refreshProject] Access denied (403)');
				
				// If we JUST claimed this project, it might be a race condition - retry once after a delay
				if (justClaimedRef.current && retryCountRef.current < 3) {
					retryCountRef.current++;
					logger.log(`[refreshProject] Just claimed - retry ${retryCountRef.current}/3 in 1 second...`);
					// Keep loading state active while retrying
					setTimeout(() => {
						refreshProject();
					}, 1000);
					return;
				}
				
				// If we reach here, it's a real access denied error
				logger.log('[refreshProject] Setting access error message');
				justClaimedRef.current = false;
				retryCountRef.current = 0;
				
				// Set error state to display on page (this will stop loading and show error)
				setAccessError(t('project.accessDeniedMessage'));
				setLoading(false);
				setMinLoadingTime(false);
				return;
			}
			
			// For other errors, just stop loading
			setLoading(false);
			setMinLoadingTime(false);
		}
	}, [projectNo]);
	
	// Initial fetch of project and related data
	// Refresh when projectNo changes OR when location changes (navigation back to page)
	
	useEffect(() => {
		// Check if this is a new project or a navigation back to the same project
		const isNewProject = lastProjectNoRef.current !== projectNo;
		const isNavigationChange = lastLocationKeyRef.current !== location.key;
		
		// Always refresh if it's a new project, or if we navigated back (location key changed)
		if (isNewProject || isNavigationChange) {
			lastProjectNoRef.current = projectNo;
			lastLocationKeyRef.current = location.key;
			refreshProject();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectNo, location.key]);
	
	/* REPLY SOLUTION: to be removed from code in future.
	// Cleanup auto-save timeout on unmount
	useEffect(() => {
		return () => {
			if (autoSaveTimeout) {
				clearTimeout(autoSaveTimeout);
			}
		};
	}, [autoSaveTimeout]);
	*/
	
	// Clear any old sessionStorage on mount
	useEffect(() => {
		sessionStorage.removeItem('prospectClaimSuccess');
	}, []);
	
	// Track mouse movement for hover tooltip
	useEffect(() => {
		if (hoverTooltip.visible) {
			const handleMouseMove = (e) => {
				setHoverTooltip(prev => ({
					...prev,
					x: e.clientX,
					y: e.clientY,
				}));
			};
			
			document.addEventListener('mousemove', handleMouseMove);
			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
			};
		}
	}, [hoverTooltip.visible]);
	
	/* Early returns - BEFORE any data access */
	
	// Show access error FIRST (before loading check)
	if (accessError) {
		return (
			<div style={{padding: '40px', textAlign: 'center'}}>
				<div style={{
					backgroundColor: '#fff5f5',
					border: '1px solid #feb2b2',
					borderRadius: '8px',
					padding: '20px',
					maxWidth: '600px',
					margin: '0 auto'
				}}>
			<h2 style={{color: '#c53030', marginTop: 0}}>{t('project.accessDenied')}</h2>
			<p style={{color: '#742a2a', fontSize: '16px'}}>{accessError}</p>
		<button 
			onClick={() => window.location.href = '/inquiries'}
				style={{
					marginTop: '20px',
					backgroundColor: '#032097',
					borderColor: '#032097',
					color: '#ffffff',
					padding: '10px 20px',
					borderRadius: '4px',
					border: '1px solid',
					cursor: 'pointer',
					fontSize: '14px',
					fontWeight: '500',
					transition: 'all 0.2s ease'
				}}
				onMouseEnter={(e) => {
					e.target.style.backgroundColor = '#f7ddda';
					e.target.style.borderColor = '#f7ddda';
					e.target.style.color = '#032097';
				}}
				onMouseLeave={(e) => {
					e.target.style.backgroundColor = '#032097';
					e.target.style.borderColor = '#032097';
					e.target.style.color = '#ffffff';
				}}
			>
				{t('project.goToInquiries')}
			</button>
				</div>
			</div>
		);
	}
	
	if (loading || minLoadingTime) {
		return <div style={{padding: '40px', textAlign: 'center', fontSize: '16px'}}>{t('project.loading')}</div>;
	}
	
	if (!project) return <div>{t('project.notFound')}</div>;
	
	// Safety check - if project.related_tasks is missing, something is wrong
	if (!project.related_tasks) {
		logger.error('Project data is incomplete, related_tasks missing');
		return <div>Loading project data...</div>;
	}
	
	/* Duration Options & Formatter */
	
	// Generate dropdown options for duration in 10-min intervals (10min to 5 hours)
	
	const durationOptions = [];

	for (let minutes = 10; minutes <= 600; minutes += 10) {
		const value = minutes; // storing minutes directly
		const hours = Math.floor(minutes / 60);
		const mins = minutes % 60;
		const display = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
		durationOptions.push({ value, display });
	}
	
	// Format minutes into "HH:MM" format for display
	
	const formatDuration = (minutes) => {
		if (!minutes || isNaN(minutes)) return '';
		const h = Math.floor(minutes / 60);
		const m = minutes % 60;
		return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
	};
	
	/* Project Status Logic */
	
	const isProjectActive = project?.status === 'active';
	const isProjectFinished = project?.status === 'finished';
	const isProjectWaiting = project?.status === 'waiting';
	const isProjectCancelled = project?.status === 'cancelled';
	const isSupport = currentUser?.role === 'support';
	const canUploadProjectFiles =
		(isProjectActive || currentUser?.role === 'admin' || isSupport) && Boolean(owner?.email);
	
	// Check if this is an unclaimed prospect (lawyer can start it)
	const isUnclaimedProspect = project?.project_type && /^(prospect|prospekt)$/i.test(project.project_type);
	// Check if this is a claimed prospect by someone else
	const isClaimedByOther = !isUnclaimedProspect && 
		project?.project_owner_id && 
		project?.project_owner_id !== currentUser?.user_no;
	
	/* REPLY SOLUTION: to be removed from code in future.
	// Check if there's a pending reply task (response required but not yet sent)
	const initialResponseSent = project?.communications?.find(c => c.type === 'initial_response');
	// Only consider reply tasks as pending if no response has been sent
	const hasReplyTaskPending = project.related_tasks?.some(t => t.status === 'reply') && !initialResponseSent;
	*/
	
	/* Paid Date Formatter */
	
	const paidDateFormatter = (cell, row) => {
		if (row.status === 'paid' && row.receipt_date) {
			return new Date(row.receipt_date).toLocaleDateString();
		}
		return '';
	};
	
	/* Generate Invoice Button Logic */
	
	/* REPLY SOLUTION: to be removed from code in future.
	// Check for unfinished tasks (waiting or reply) - with null safety
	const hasUnfinishedTasks = project.related_tasks?.some((task) => task.status === 'waiting' || task.status === 'reply') || false;
	*/
	const hasWaitingTasks = project.related_tasks?.some((task) => task.status === 'waiting') || false;
	/* REPLY SOLUTION: to be removed from code in future.
	// Only consider reply tasks as blocking if no response has been sent
	const hasReplyTasks = (project.related_tasks?.some((task) => task.status === 'reply') && !initialResponseSent) || false;
	*/
	const hasUnbilledTasks = project.related_tasks?.some((task) => !task.task_billed) || false;
	
	const canGenerateInvoice =
		isProjectActive &&
		hasUnbilledTasks &&
		!hasWaitingTasks;
		
	/* Finish Project Logic */
	
	const canFinishProject =
		isProjectActive &&
		project?.related_tasks?.length > 0 &&
		project?.related_tasks?.every((task) => task.task_billed) &&
		!project?.related_tasks?.some((task) => task.status === 'waiting');
	
	/* PDF Download Handler */
	
	const handleDownloadPDF = (invoiceId) => {
		// Create a direct download link without authentication
		let baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:4000';
		
		// Remove trailing /api if it exists to avoid double /api
		if (baseUrl.endsWith('/api')) {
			baseUrl = baseUrl.replace('/api', '');
		}
		
		const pdfUrl = `${baseUrl}/api/invoices/pdf/${invoiceId}`;
		logger.log('PDF URL:', pdfUrl);
		window.open(pdfUrl, '_blank');
	};
		
	/* Table Column Definitions */
	
	// Prepayments Table Columns
	
	const prepaymentInvoiceColumns = [
		{
			dataField: 'issue_date',
			text: t('project.table.date'),
			classes: 'cell-date',
			headerClasses: 'cell-date',
			formatter: d => formatDateDMY(d),
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'amount_net',
			text: t('project.table.net'),
			classes: 'cell-net',
			headerClasses: 'cell-net',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'vat',
			text: t('project.table.vat'),
			classes: 'cell-vat',
			headerClasses: 'cell-vat',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'amount_total',
			text: t('project.table.total'),
			classes: 'cell-total',
			headerClasses: 'cell-total',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'due_date',
			text: t('project.table.dueDate'),
			classes: 'cell-due-date',
			headerClasses: 'cell-due-date',
			formatter: d => formatDateDMY(d),
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'amountBilled',
			text: t('project.table.retainer'),
			classes: 'cell-retainer',
			headerClasses: 'cell-retainer',
			formatter: (cell, row) => {
				const available = getPrepaymentAvailable(row);
				if (available <= 0) {
					return '-';
				}
				const display = `${Math.max(0, available).toFixed(0)}`;
				if (row.status !== 'paid' && available > 0) {
					return <span title={t('project.prepayment.partialTooltip')}>{display}</span>;
				}
				return display;
			},
			align: 'left',
			headerAlign: 'left',
			style: { width: '200px', minWidth: '200px', maxWidth: '200px' },
			headerStyle: { width: '200px', minWidth: '200px', maxWidth: '200px' },
		},
		{
			dataField: 'billed',
			text: t('project.table.billed'),
			classes: 'cell-billed',
			headerClasses: 'cell-billed',
			formatter: (cell) => (cell ? t('common.yes') : t('common.no')),
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'invoiceNumber',
			text: t('project.table.invoice'),
			classes: 'cell-no',
			headerClasses: 'cell-no',
			align: 'left',
			headerAlign: 'left',
			style: { width: '120px', minWidth: '120px', maxWidth: '120px' },
			headerStyle: { width: '120px', minWidth: '120px', maxWidth: '120px' },
		},
		{
			dataField: 'status',
			text: t('project.table.status'),
			classes: 'cell-status',
			headerClasses: 'cell-status',
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'action',
			text: t('project.table.actions'),
			classes: 'cell-actions',
			headerClasses: 'cell-actions',
			sort: false,
			align: 'left',
			headerAlign: 'left',
			style: { width: 'auto', minWidth: '100px' },
			headerStyle: { width: 'auto', minWidth: '100px' },
			formatter: (cell, row) => {
				const buttons = [];
				
				// PDF button - always available
				buttons.push(
					<button
						key="pdf"
						className="button button-small"
						onClick={() => handleDownloadPDF(row._id)}
						title={t('project.downloadPdf')}
					>
						PDF
					</button>
				);
				
				// Send button for draft prepayments
				if (!isSupport && row.type === 'prepayment' && row.status === 'draft') {
					buttons.push(
						<button
							key="send"
							className="button button-small"
							onClick={() => handleSendPrepayment(row)}
						>
							{t('common.send')}
						</button>
					);
				}
				
				// Credit button for paid/sent prepayments with remaining balance, or unpaid sent (cancellation)
				const prepaymentAvailable = getPrepaymentAvailable(row);
				const isUnpaidSentPrepayment = row.status === 'sent' && getPrepaymentReceived(row) === 0;
				const canCreditPrepayment = prepaymentAvailable > 0 || isUnpaidSentPrepayment;
				if (!isSupport && row.type === 'prepayment' && (row.status === 'paid' || row.status === 'sent') && row.status !== 'credited' && canCreditPrepayment) {
					const canCredit = (isProjectActive || currentUser?.role === 'admin') && !isSupport;
					buttons.push(
						<button
							key="credit"
							className={`button button-small${!canCredit ? ' inactive' : ''}`}
							onClick={canCredit ? () => handleCreditInvoice(row) : (e) => { e.preventDefault(); e.stopPropagation(); }}
							disabled={!canCredit}
							title={!canCredit ? t('project.startToCreditPrepayments') : ""}
						>
							{t('project.credit')}
						</button>
					);
				}
				
				return buttons.length > 0 ? (
					<div className="actions">
						{buttons}
					</div>
				) : null;
			},
		},
	];
	
	// Regular Invoices Table Columns
	
	const invoiceColumns = [
		{
			dataField: 'createdAt',
			text: t('project.table.date'),
			classes: 'cell-date',
			headerClasses: 'cell-date',
			formatter: d => formatDateDMY(d),
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'amount_net',
			text: t('project.table.net'),
			classes: 'cell-net',
			headerClasses: 'cell-net',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'vat',
			text: t('project.table.vat'),
			classes: 'cell-vat',
			headerClasses: 'cell-vat',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'amount_total',
			text: t('project.table.total'),
			classes: 'cell-total',
			headerClasses: 'cell-total',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'due_date',
			text: t('project.table.dueDate'),
			classes: 'cell-due-date',
			headerClasses: 'cell-due-date',
			formatter: d => formatDateDMY(d),
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'prepaymentApplied',
			text: t('project.table.offset'),
			classes: 'cell-offset',
			headerClasses: 'cell-offset',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '200px', minWidth: '200px', maxWidth: '200px' },
			headerStyle: { width: '200px', minWidth: '200px', maxWidth: '200px' },
		},
		{
			dataField: 'amountDue',
		text: t('project.table.balance'),
		classes: 'cell-balance',
		headerClasses: 'cell-balance',
		formatter: (value, row) => {
			// If status is paid or credited, balance is 0
			if (row.status === 'paid' || row.status === 'credited') {
				return '0.00';
			}
			
			// Calculate balance: amount_total - sum of all payment transactions
			// This is the source of truth for balance calculation
			const amountTotal = parseFloat(row.amount_total || 0);
			let totalPayments = 0;
			
			// Sum all payment transactions
			if (row.payment_transactions && Array.isArray(row.payment_transactions)) {
				totalPayments = row.payment_transactions.reduce((sum, transaction) => {
					return sum + parseFloat(transaction.amount || 0);
				}, 0);
			}
			
			// Balance = amount_total - sum of payment transactions
			const balance = amountTotal - totalPayments;
			
			// Ensure balance doesn't go negative (display 0 if negative), use precise 2 decimal places
			return balance > 0 ? `${balance.toFixed(2)}` : '0.00';
		},
		align: 'left',
		headerAlign: 'left',
		style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'invoiceNumber',
			text: t('project.table.invoice'),
			classes: 'cell-no',
			headerClasses: 'cell-no',
			align: 'left',
			headerAlign: 'left',
			style: { width: '120px', minWidth: '120px', maxWidth: '120px' },
			headerStyle: { width: '120px', minWidth: '120px', maxWidth: '120px' },
		},
		{
			dataField: 'status',
			text: t('project.table.status'),
			classes: 'cell-status',
			headerClasses: 'cell-status',
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'action',
			text: t('project.table.actions'),
			classes: 'cell-actions',
			headerClasses: 'cell-actions',
			sort: false,
			align: 'left',
			headerAlign: 'left',
			style: { width: 'auto', minWidth: '100px' },
			headerStyle: { width: 'auto', minWidth: '100px' },
			formatter: (cell, row) => {
				const buttons = [];
				
				// PDF button - always available
				buttons.push(
					<button
						key="pdf"
						className="button button-small"
						onClick={() => handleDownloadPDF(row._id)}
						title={t('project.downloadPdf')}
					>
						PDF
					</button>
				);
				
				// Send button for issued or draft invoices - disabled when project not started
				if (!isSupport && row.type === 'invoice' && (row.status === 'issued' || row.status === 'draft')) {
					buttons.push(
						<button
							key="send"
							className={`button button-small${!isProjectActive ? ' inactive' : ''}`}
							onClick={isProjectActive ? () => handleSendInvoice(row) : (e) => { e.preventDefault(); e.stopPropagation(); }}
							disabled={!isProjectActive}
							title={!isProjectActive ? t('project.startToSendInvoices') : ""}
						>
							{t('common.send')}
						</button>
					);
				}
				
				// Credit button for paid or sent invoices (not already credited) - disabled for lawyers when project not started
				if (!isSupport && row.type === 'invoice' && (row.status === 'paid' || row.status === 'sent') && row.status !== 'credited') {
					const canCredit = (isProjectActive || currentUser?.role === 'admin') && !isSupport;
					buttons.push(
						<button
							key="credit"
							className={`button button-small${!canCredit ? ' inactive' : ''}`}
							onClick={canCredit ? () => handleCreditInvoice(row) : (e) => { e.preventDefault(); e.stopPropagation(); }}
							disabled={!canCredit}
							title={!canCredit ? t('project.startToCreditInvoices') : ""}
						>
							{t('project.credit')}
						</button>
					);
				}
				
				return buttons.length > 0 ? (
					<div className="actions">
						{buttons}
					</div>
				) : null;
			},
		},
	];
	
	// Credit Invoices Table Columns
	
	const creditInvoiceColumns = [
		{
			dataField: '_id',
			text: 'ID',
			classes: 'cell-id',
			headerClasses: 'cell-id',
			hidden: true,
		},
		{
			dataField: 'issue_date',
			text: t('project.table.date'),
			classes: 'cell-date',
			headerClasses: 'cell-date',
			formatter: d => formatDateDMY(d),
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'amount_net',
			text: t('project.table.net'),
			classes: 'cell-net',
			headerClasses: 'cell-net',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'vat',
			text: t('project.table.vat'),
			classes: 'cell-vat',
			headerClasses: 'cell-vat',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'amount_total',
			text: t('project.table.total'),
			classes: 'cell-total',
			headerClasses: 'cell-total',
			formatter: (value) => value != null ? `${value}` : '0',
			align: 'left',
			headerAlign: 'left',
			style: { width: '80px', minWidth: '80px', maxWidth: '80px' },
			headerStyle: { width: '80px', minWidth: '80px', maxWidth: '80px' },
		},
		{
			dataField: 'spacer',
			text: '',
			classes: 'cell-spacer',
			headerClasses: 'cell-spacer',
			formatter: () => '',
			align: 'left',
			headerAlign: 'left',
			style: { width: '200px', minWidth: '200px', maxWidth: '200px' },
			headerStyle: { width: '200px', minWidth: '200px', maxWidth: '200px' },
		},
		{
			dataField: 'related_invoice_no',
			text: t('project.table.crediting'),
			classes: 'cell-crediting',
			headerClasses: 'cell-crediting',
			formatter: (value) => value || '-',
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'invoiceNumber',
			text: t('project.table.invoice'),
			classes: 'cell-no',
			headerClasses: 'cell-no',
			align: 'left',
			headerAlign: 'left',
			style: { width: '120px', minWidth: '120px', maxWidth: '120px' },
			headerStyle: { width: '120px', minWidth: '120px', maxWidth: '120px' },
		},
		{
			dataField: 'status',
			text: t('project.table.status'),
			classes: 'cell-status',
			headerClasses: 'cell-status',
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'action',
			text: t('project.table.actions'),
			classes: 'cell-actions',
			headerClasses: 'cell-actions',
			sort: false,
			align: 'left',
			headerAlign: 'left',
			style: { width: 'auto', minWidth: '100px' },
			headerStyle: { width: 'auto', minWidth: '100px' },
			formatter: (cell, row) => {
				return (
					<div className="actions">
						<button
							className="button button-small"
							onClick={() => handleDownloadPDF(row._id)}
							title={t('project.downloadPdf')}
						>
							PDF
						</button>
					</div>
				);
			},
		},
	];
	
	// Tasks Table Columns – using field "task_name" and formatting duration (from minutes)
	
	const taskColumns = [
		{
			dataField: 'task_date',
			text: t('project.table.date'),
			classes: 'cell-date',
			headerClasses: 'cell-date',
			formatter: d => formatDateDMY(d),
			sort: true,
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'task_name',
			text: t('project.table.description'),
			classes: 'cell-name',
			headerClasses: 'cell-name',
			align: 'left',
			headerAlign: 'left',
			style: {
				whiteSpace: 'normal',
				wordWrap: 'break-word',
				overflowWrap: 'break-word',
				width: '240px',
				minWidth: '240px',
				maxWidth: '240px',
				textAlign: 'left'
			},
			headerStyle: { width: '240px', minWidth: '240px', maxWidth: '240px' },
		},
		{
			dataField: 'deadline',
			text: t('project.table.deadline'),
			classes: 'cell-deadline',
			headerClasses: 'cell-deadline',
			formatter: d => formatDateDMY(d),
			sort: true,
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'task_duration',
			text: t('project.table.duration'),
			classes: 'cell-duration',
			headerClasses: 'cell-duration',
			formatter: (cell) => formatDuration(parseFloat(cell)),
			align: 'left',
			headerAlign: 'left',
			style: { width: '200px', minWidth: '200px', maxWidth: '200px' },
			headerStyle: { width: '200px', minWidth: '200px', maxWidth: '200px' },
		},
		{
			dataField: 'task_billed',
			text: t('project.table.billed'),
			classes: 'cell-billed',
			headerClasses: 'cell-billed',
			formatter: (cell, row) => {
				if (row.task_credited) return t('project.table.credited');
				return cell ? t('common.yes') : t('common.no');
			},
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'related_invoice',
			text: t('project.table.invoice'),
			classes: 'cell-invoice',
			headerClasses: 'cell-invoice',
			formatter: (cell) => cell || '',
			align: 'left',
			headerAlign: 'left',
			style: { width: '120px', minWidth: '120px', maxWidth: '120px' },
			headerStyle: { width: '120px', minWidth: '120px', maxWidth: '120px' },
		},
		{
			dataField: 'status',
			text: t('project.table.status'),
			classes: 'cell-status',
			headerClasses: 'cell-status',
			align: 'left',
			headerAlign: 'left',
			style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
			headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
		},
		{
			dataField: 'actions',
			text: t('project.table.actions'),
			classes: 'cell-actions',
			headerClasses: 'cell-actions',
			sort: false,
			align: 'left',
			headerAlign: 'left',
			style: { width: 'auto', minWidth: '100px' },
			headerStyle: { width: 'auto', minWidth: '100px' },
			formatter: (cell, row) => {
				const buttons = [];
				
				/* REPLY SOLUTION: to be removed from code in future.
				// Reply button: For tasks with status 'reply' only if reply hasn't been sent yet
				if (row.status === 'reply' && !initialResponseSent) {
					buttons.push(
						<button className="button button-small button-blue" onClick={openInitialResponseModal}>
							{t('common.reply')}
						</button>
					);
				}
				*/
				
				// Edit button: unbilled tasks, or billed invoice lines within edit window
				const linkedInvoice = row.related_invoice
					? invoices.find((inv) => inv.invoiceNumber === row.related_invoice)
					: null;
				const canEditInvoiceLineTask = canEditInvoiceLine(row, linkedInvoice);
				const canEdit = !isSupport && (isProjectActive || currentUser?.role === 'admin') && (!row.task_billed || canEditInvoiceLineTask);
				const editTitle = canEditInvoiceLineTask && getInvoiceEditWindowEnd(linkedInvoice)
					? t('project.task.editInvoiceLineUntil', { time: formatDateDMYTime(getInvoiceEditWindowEnd(linkedInvoice)) })
					: undefined;
				if (canEdit) {
					buttons.push(
						<button
							className="button button-small"
							title={editTitle}
							onClick={() => handleEditTaskClick(row, canEditInvoiceLineTask)}
						>
						{t('common.edit')}
						</button>
					);
				}
				
				// Finish button: For any waiting task, even if billed (billed waiting tasks can still be finished)
				// Admins can finish even when project not started
				if (!isSupport && (isProjectActive || currentUser?.role === 'admin') && row.status === 'waiting') {
					buttons.push(
						<button className="button button-small" onClick={(e) => handleFinishTask(row, e)}>
						{t('common.finish')}
						</button>
					);
				}

				// Finish button for reply tasks: admin only
				if (currentUser?.role === 'admin' && row.status === 'reply') {
					buttons.push(
						<button className="button button-small" onClick={(e) => handleFinishTask(row, e)}>
						{t('common.finish')}
						</button>
					);
				}
				
				// Delete button: Only for unbilled tasks (billed tasks can't be deleted)
				const canDelete = !isSupport && (isProjectActive || currentUser?.role === 'admin') && !row.task_billed;
				if (canDelete) {
					buttons.push(
						<button className="button button-small" onClick={() => handleDeleteTask(row)}>
							{t('common.delete')}
						</button>
					);
				}
				
				return (
				<>
					<div className="actions">
						{buttons.map((btn, index) => (
							React.cloneElement(btn, { key: index })
						))}
						{/*
						{buttons.map((btn, index) => (
							<span key={index}>
								{btn}
							</span>
						))}
						*/}
					</div>
				</>
				);
			},
		},
	];
	
	const confirmCancel = (title, text, onYes)=> {
			let n; n = window.nConfirm({
				title,
				text,
				icon:'fa-exclamation-circle',
				onYes: async()=> { await onYes(); n?.close(); setShowActionMenu(false)},
				onNo : ()=> { n?.close();  setShowActionMenu(false)}
			});
		};
		
	/* Unified Task Modal Functions */
	
	const openAddTaskModal = () => {
		setTaskModalData({
			task_name: '',
			task_duration: '',
			status: 'done',
			deadline: '',
			task_date: '',
			task_billed: false
		});
		setIsEditingTask(false);
		setIsInvoiceLineEdit(false);
		setTaskModalErrors({});
		setShowTaskModal(true);
	};
	
	const handleEditTaskClick = (taskItem, invoiceLineEdit = false) => {
		/* REPLY SOLUTION: to be removed from code in future.
		// Prevent lawyers from editing reply type tasks
		if (currentUser?.role === 'lawyer' && taskItem.status === 'reply') {
			window.nError({
				title: t('project.accessDenied'),
				text: t('project.task.cannotEditReply'),
				icon: 'fa-exclamation-triangle'
			});
			return;
		}
		*/
		
		setTaskModalData({
			...taskItem,
			originalRef: taskItem,
			deadline: taskItem.deadline ? new Date(taskItem.deadline).toISOString().split('T')[0] : '',
			task_date: taskItem.task_date ? new Date(taskItem.task_date).toISOString().split('T')[0] : ''
		});
		setIsEditingTask(true);
		setIsInvoiceLineEdit(invoiceLineEdit);
		setTaskModalErrors({});
		setShowTaskModal(true);
	};
	
	const handleSaveTaskModal = async () => {
		
		// Validate mandatory fields
		
		let errors = {};
		
		if (!taskModalData.task_name.trim()) errors.task_name = t('project.task.nameRequired');
		if (!isInvoiceLineEdit && !taskModalData.task_duration) errors.task_duration = t('project.task.durationRequired');
		const effectiveStatus = isInvoiceLineEdit ? (taskModalData.originalRef?.status || 'done') : taskModalData.status;
		if (effectiveStatus === 'done' && !taskModalData.task_date)
		errors.task_date = t('project.task.dateRequiredWhenDone');
	
		if (Object.keys(errors).length > 0) {
			setTaskModalErrors(errors);
			return;
		}
		
		if (isEditingTask) {
			const updatedTasks = project.related_tasks.map((t) => {
				if (t === taskModalData.originalRef) {
					if (isInvoiceLineEdit) {
						return {
							...t,
							task_name: taskModalData.task_name,
							task_date: taskModalData.task_date ? new Date(taskModalData.task_date) : null,
						};
					}
					return {
						...t,
						task_name: taskModalData.task_name,
						task_duration: taskModalData.task_duration,
						status: taskModalData.status,
						deadline: taskModalData.deadline ? new Date(taskModalData.deadline) : null,
						task_date: taskModalData.task_date ? new Date(taskModalData.task_date) : null,
						task_billed: taskModalData.task_billed || false
					};
				}
				return t;
			});
			
			try {
				const response = await axios.patch(`/projects/${project._id}`, {
					related_tasks: updatedTasks,
				});
				
				setProject((prev) => applyProjectTaskPatchResponse(prev, response));
				
				setShowTaskModal(false);
				setTaskModalData(null);
				setIsInvoiceLineEdit(false);
				setTaskModalErrors({});
			} catch (error) {
				logger.error('Error saving edited task:', error);
				window.nError({
					title: t('common.errorTitle'),
					text: error.response?.data?.message || t('project.task.editWindowExpired'),
					icon: 'fa-exclamation-triangle',
				});
			}
		} else {
			const newTask = {
				task_id: crypto.randomUUID(), // unique
				task_name: taskModalData.task_name,
				task_duration: parseFloat(taskModalData.task_duration) || 0,
				status: taskModalData.status,
				deadline: taskModalData.deadline ? new Date(taskModalData.deadline) : null,
				task_date: taskModalData.task_date ? new Date(taskModalData.task_date) : null,
				task_billed: taskModalData.task_billed || false,
			};
			
			const updatedTasks = [...project.related_tasks, newTask];
			
			try {
				const response = await axios.patch(`/projects/${project._id}`, {
					related_tasks: updatedTasks,
				});
				
				setProject((prev) => applyProjectTaskPatchResponse(prev, response));
				
				setShowTaskModal(false);
				setTaskModalData(null);
				setTaskModalErrors({});
			} catch (error) {
				logger.error('Error adding task:', error);
			}
		}
	};
	
	/* Show tooltip on click for disabled buttons */
	
	const showClickTooltip = (event, message) => {
		event.preventDefault();
		event.stopPropagation();
		
		setClickTooltip({
			visible: true,
			x: event.clientX,
			y: event.clientY,
			message: message,
		});
		
		setTimeout(() => {
			setClickTooltip({ visible: false, x: 0, y: 0, message: "" });
		}, 3000);
	};
	
	/* Show hover tooltip that follows mouse cursor */
	
	const showHoverTooltip = (event, message) => {
		setHoverTooltip({
			visible: true,
			x: event.clientX,
			y: event.clientY,
			message: message,
		});
	};
	
	const hideHoverTooltip = () => {
		setHoverTooltip({ visible: false, x: 0, y: 0, message: "" });
	};
	
	/* Finish Task Function */
	
	const handleFinishTask = async (taskItem, event) => {
		logger.log('handleFinishTask called with:', taskItem);
		
		// Get today's date in ISO format
		const today = new Date().toISOString();
		
		let taskFound = false;
		const updatedTasks = project.related_tasks.map((t) => {
			
			// Match by ID if possible
			const isMatchById = taskItem.task_id && t.task_id && t.task_id === taskItem.task_id;
			
			// Fallback match if no ID - be more flexible with date comparison
			const isMatchByFields = !taskItem.task_id && !t.task_id && (
				t.task_name === taskItem.task_name &&
				parseFloat(t.task_duration) === parseFloat(taskItem.task_duration)
			);
			
			// Additional fallback - match by name and duration only
			const isMatchByNameDuration = (
				t.task_name === taskItem.task_name &&
				parseFloat(t.task_duration) === parseFloat(taskItem.task_duration) &&
				(t.status === 'waiting' || t.status === 'reply') &&
				(!taskItem.status || t.status === taskItem.status)
			);
			
			if (isMatchById || isMatchByFields || isMatchByNameDuration) {
				logger.log('Task matched, updating status to done');
				taskFound = true;
				// Set task as done and automatically set today's date if no date exists
				return { 
					...t, 
					status: 'done',
					task_date: t.task_date || today
				};
			}
			
			return t;
		});
		
		if (!taskFound) {
			logger.error('Task not found for finishing:', taskItem);
			window.nError({title: t('common.errorTitle'), text: t('project.task.notFound'), icon: 'fa-exclamation-triangle'});
			return;
		}
		
		try {
			logger.log('Sending update to server...');
			const response = await axios.patch(`/projects/${project._id}`, {related_tasks: updatedTasks});
			setProject((prev) => applyProjectTaskPatchResponse(prev, response));
			window.nSuccess({title: t('common.successTitle'), text: t('project.task.finishedSuccess'), icon: 'fa-check-square'});
			logger.log('Task finished successfully');
		} catch (error) {
			logger.error('Error finishing task:', error);
			window.nError({title: t('common.errorTitle'), text: t('project.task.finishError'), icon: 'fa-exclamation-triangle'});
		}
	};
	
	/* Delete Task Function */
	
	const handleDeleteTask = async (taskToDelete) => {
		/* REPLY SOLUTION: to be removed from code in future.
		// Prevent lawyers from deleting reply type tasks
		if (currentUser?.role === 'lawyer' && taskToDelete.status === 'reply') {
			window.nError({
				title: t('project.accessDenied'),
				text: t('project.task.cannotDeleteReply'),
				icon: 'fa-exclamation-triangle'
			});
			return;
		}
		*/
		
		// Ask for confirmation before deleting
		let n; n = window.nConfirm({
			title: t('project.task.deleteConfirmTitle'),
			text: `<span>${t('project.task.deleteConfirmText', { name: taskToDelete.task_name })}</span>`,
			icon: 'fa-exclamation-circle',
			onYes: async () => { 
				await deleteTaskConfirmed(taskToDelete); 
				n?.close(); 
			},
			onNo: () => { n?.close(); }
		});
	};

	const deleteTaskConfirmed = async (taskToDelete) => {
		const updatedTasks = project.related_tasks.filter((t) => {
			
			// Primary method: Match by ID if available
			
			if (taskToDelete.task_id && t.task_id) {return t.task_id !== taskToDelete.task_id;}
			
			// Fallback: Match by key fields
			
			return !(
				t.task_name === taskToDelete.task_name &&
				new Date(t.task_date).toISOString() === new Date(taskToDelete.task_date).toISOString() &&
				parseFloat(t.task_duration) === parseFloat(taskToDelete.task_duration)
			);
		});
		
		logger.log("Task to delete:", taskToDelete);
		logger.log("Remaining tasks:", updatedTasks);
		
		try {
			const response = await axios.patch(`/projects/${project._id}`, {related_tasks: updatedTasks,});
			setProject((prev) => applyProjectTaskPatchResponse(prev, response));
			window.nSuccess({title: t('common.successTitle'), text: t('project.task.deletedSuccess'), icon: 'fa-check-square'});
		} catch (error) {
			logger.error('Error deleting task:', error);
			window.nError({title: t('common.errorTitle'), text: t('project.task.deleteError'), icon: 'fa-exclamation-triangle'});
		}
	};
	
	/* Prepayment Functions */
	
	const onGeneratePrepayment = () => setShowPrepaymentModal(true);
	const getVatPercentage = () => (owner && owner.partner_vat ? 0.24 : 0);
	
	const confirmPrepayment = async () => {
		try {
			setIsPrepaymentProcessing(true);
			// Calculate total minutes from hours and minutes
			const totalMinutes = (prepaymentHours * 60) + prepaymentMinutes;
			const hourlyRate = getEffectiveHourlyRate();
			const totalAmount = ((totalMinutes / 60) * hourlyRate).toFixed(2);
			const vatPercentage = getVatPercentage();
			
			const invoiceData = {
				projectId: project._id,
				projectNo: project.project_no,
				clientEmail: client?.email || project?.client_email || '',
				clientName: getRepresentativeName(),
				projectName: project.project_name,
				amount: totalAmount,
				clientId: project.related_client_id,
				partner_id: project.project_owner_no,
			};
			
			const response = await generatePrepayment(invoiceData, vatPercentage);
			
			if (!response.invoiceNumber) {
				logger.warn("Error: Prepayment generation failed");
				setPrepaymentFeedback("Error generating prepayment in system.");
				return;
			}
			
			const invoiceNumber = response.invoiceNumber;
			
			setPrepaymentFeedback(`Prepayment ${invoiceNumber} created in draft state. Sending email...`);
			
			// Calculate hours and minutes for display
			const hoursMinutes = `${prepaymentHours}:${prepaymentMinutes.toString().padStart(2, '0')}`;
		
		/* OLD: localhost */
		/* const botId = 'botId67af398d24fea1179c8ae2f7'; // localhost
		const paymentLink = `http://localhost:3001/?bot=${botId}&project_no=${invoiceData.projectNo}&service=${encodeURIComponent(
			invoiceData.projectName
		)}&selected_service_price=${invoiceData.amount}&invoice_no=${invoiceNumber}`; */
		
		/* OLD: LIVE - Commented out: sending all data in link */
		/* const botId = '67cc2842c7a21d8e097693d7';
		const paymentLink = `https://app.hugo.legal/hugoapp?bot=${botId}&project_no=${invoiceData.projectNo}&service=${encodeURIComponent(
			invoiceData.projectName
		)}&selected_service_price=${invoiceData.amount}&invoice_no=${invoiceNumber}&invoice_type=prepayment&partner_name=${owner.firstName + ' ' + owner.lastName}&partner_email=${owner.email}&client_type=${project.client_type || 'person'}`; */
		
		/* NEW: ID-based solution - only invoice _id in URL */
		const paymentLink = buildInvoicePaymentLink(response._id);
		
		/* NEW: localhost */
		/* const botId = 'botId67af398d24fea1179c8ae2f7';
		const paymentLink = `http://localhost:3001/?bot=${botId}&invoice_id=${response._id}`; */
			const issue_date = new Date();
			const due_date = new Date(issue_date.getTime() + prepaymentDueDays * 24 * 60 * 60 * 1000);
			const sendpayment = await axios.post(`/projects/${project._id}/sendPrepayment`, {
				email: client?.email || project?.client_email || '',
				client: getRepresentativeName(),
				client_type: project.client_type,
				business_name: client?.business_name || project?.business_name || '',
				amount: response.amount_total,
				payment_description: prepaymentComment || 'Ettemaks õigusteenuse eest',
				billable_hours: hoursMinutes,
				invoice_no: invoiceNumber,
				invoice_id: response._id,
				owner: project.project_owner,
				partner_hourly_rate: getEffectiveHourlyRate(),
				partner_name: owner.partner_name,
				partner_company: owner.partner_company_name,
				partner_company_reg_id: owner.partner_company_reg_id,
				partner_company_address: owner.partner_company_address,
				vat: response.vat,
				link: paymentLink,
				due_date,
				status: 'sent',
			});
			
		if (sendpayment.error) {
			setPrepaymentFeedback(`Prepayment ${invoiceNumber} EMAIL NOT SENT`);
			const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
			const prepaymentInvoices = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'prepayment')
			: [];
			const creditInvoicesData = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
			setPrepayments(prepaymentInvoices);
			setCreditInvoices(creditInvoicesData);
			return;
		}
			
		await axios.put(`/invoices/${response._id}`, {
			status: 'sent',
			issue_date,
			due_date,
		});
		
		const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
		const updatedPrepayments = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'prepayment')
			: [];
		const creditInvoicesData = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
		setPrepayments(updatedPrepayments);
		setCreditInvoices(creditInvoicesData);
		
		setPrepaymentFeedback(`Prepayment ${invoiceNumber} email sent and updated successfully.`);
		} catch (error) {
			logger.error('Error generating prepayment:', error);
		setPrepaymentFeedback("Error generating prepayment: " + error.message);
		} finally {
			setIsPrepaymentProcessing(false);
		}
	};
	
const handleSendPrepayment = async (invoice) => {
	try {
		setIsPrepaymentProcessing(true);
		
		/* OLD: localhost - Commented out: sending all data in link */
		/* const botId = 'botId67af398d24fea1179c8ae2f7';
		const paymentLink = `http://localhost:3001/?bot=${botId}&project_no=${project.project_no}&service=${encodeURIComponent(
			project.project_name
		)}&selected_service_price=${invoice.amount_total}&invoice_no=${invoice.invoiceNumber}&invoice_type=prepayment&partner_name=${owner.firstName + ' ' + owner.lastName}&partner_email=${owner.email}&client_type=${project.client_type || 'person'}`; */
		
		/* OLD: LIVE - Commented out: sending all data in link */
		/* const botId = '67cc2842c7a21d8e097693d7';
		const paymentLink = `https://app.hugo.legal/hugoapp?bot=${botId}&project_no=${project.project_no}&service=${encodeURIComponent(
			project.project_name
		)}&selected_service_price=${invoice.amount_total}&invoice_no=${invoice.invoiceNumber}&client_type=${project.client_type || 'person'}`; */
		
		/* NEW: ID-based solution - only invoice _id in URL */
		const paymentLink = buildInvoicePaymentLink(invoice._id);
		
		/* NEW: localhost */
		/* const botId = 'botId67af398d24fea1179c8ae2f7';
		const paymentLink = `http://localhost:3001/?bot=${botId}&invoice_id=${invoice._id}`; */
			
			const sendpayment = await axios.post(`/projects/${project._id}/sendPrepayment`, {
				email: client?.email || project?.client_email || '',
				client: getRepresentativeName(),
				client_type: project.client_type,
				business_name: client?.business_name || project?.business_name || '',
				amount: invoice.amount_total,
				payment_description: invoice.payment_description || 'Ettemaks õigusteenuse eest',
				billable_hours: invoice.billable_hours || '',
				invoice_no: invoice.invoiceNumber,
				invoice_id: invoice._id,
				owner: project.project_owner,
				partner_hourly_rate: getEffectiveHourlyRate(),
				partner_name: owner.partner_name,
				partner_company: owner.partner_company_name,
				partner_company_reg_id: owner.partner_company_reg_id,
				partner_company_address: owner.partner_company_address,
				vat: invoice.vat,
				link: paymentLink,
				due_date: invoice.due_date || (() => { const d = new Date(); return new Date(d.getTime() + prepaymentDueDays * 24 * 60 * 60 * 1000); })(),
				status: invoice.status || 'sent',
			});
			
		if (sendpayment.error) {
			setPrepaymentFeedback(`Prepayment EMAIL NOT SENT`);
			const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
			const prepaymentInvoices = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'prepayment')
			: [];
			const creditInvoicesData = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
			setPrepayments(prepaymentInvoices);
			setCreditInvoices(creditInvoicesData);
			return;
		}
			
			const issue_date = new Date();
			const due_date = new Date(issue_date.getTime() + prepaymentDueDays * 24 * 60 * 60 * 1000);
		await axios.put(`/invoices/${invoice._id}`, {
			status: 'sent',
			issue_date,
			due_date,
		});
		
		setPrepaymentFeedback(`Prepayment ${invoice.invoiceNumber} email sent and updated successfully.`);
		const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
		const updatedPrepayments = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'prepayment')
			: [];
		const creditInvoicesData = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
		setPrepayments(updatedPrepayments);
		setCreditInvoices(creditInvoicesData);
			} catch (error) {
				logger.error("Error sending prepayment email:", error);
				setPrepaymentFeedback("Error sending prepayment email: " + error.message);
			} finally {
				setIsPrepaymentProcessing(false);
		}
	};
	
	const generatePrepayment = async (invoiceData, vatPercentage) => {
		// Calculate amounts with precise numbers, only round at the end
		const amountTotal = parseFloat(invoiceData.amount);
		const amountNet = amountTotal / (1 + vatPercentage);
		const vatAmount = amountTotal - amountNet;
		
		// Calculate hours and minutes for display
		const hoursMinutes = `${prepaymentHours}:${prepaymentMinutes.toString().padStart(2, '0')}`;
		
		const response = await axios.post('/invoices/create', {
			related_project_id: invoiceData.projectId,
			related_project_no: invoiceData.projectNo,
			client_id: invoiceData.clientId,
			client_name: invoiceData.clientName,
			client_email: client?.email || project?.client_email || '',
			client_type: project.client_type === 'business' ? 'business' : 'individual', // Map client types: person->individual, business->business
			client_pid: client?.pid || project?.client_pid || '',
			// Add business fields if client is a business
			...(project.client_type === 'business' && {
				business_name: client?.business_name || project?.business_name || '',
				business_address: client?.business_address || project?.business_address || '',
				business_reg_code: client?.business_reg_code || project?.business_reg_code || '',
			}),
			amount_total: amountTotal.toFixed(2),
			amount_net: amountNet.toFixed(2),
			vat: vatAmount.toFixed(2),
			type: 'prepayment',
			payment_description: prepaymentComment || 'Ettemaks õigusteenuse eest',
			billable_hours: hoursMinutes,
			hourly_rate: getEffectiveHourlyRate(),
			related_partner_id: owner.user_no,
			partner_company_name: owner.partner_company_name,
			partner_name: owner.firstName + ' ' + owner.lastName,
			service: invoiceData.projectName,
			status: 'draft',
		});
		logger.log('Prepayment invoice generated:', response);
		return response;
	};
	
	/* Invoice Generation */

	const onGenerateInvoice = () => {
		// Clear any previous invoice feedback
		setInvoiceFeedback('');
		
		const unbilledTasks = project.related_tasks.filter(t => !t.task_billed);
		const tasksTotal    = unbilledTasks.reduce((sum, t) => {
			const minutes = parseFloat(t.task_duration) || 0;
			return sum + (minutes / 60) * getEffectiveHourlyRate();
		}, 0);
		
		// Figure out how much prepayment credit is actually still available

		const availablePrepayments = prepayments
		.filter(p => p.status === 'paid')
		.map(p => getPrepaymentAvailable(p));
		
		const totalPrepaymentsAvailable = availablePrepayments.reduce((sum, r) => sum + r, 0);
		// we can only bill up to the lesser of tasksTotal or prepayment credit
		const prepaymentBilled = Math.min(totalPrepaymentsAvailable, tasksTotal);
		
		// invoice the remainder
		const invoiceAmount = (tasksTotal.toFixed(2));
		
		setUnbilledTasksTotal(tasksTotal.toFixed(2));
		setPrepaymentAmountBilled(prepaymentBilled.toFixed(2)); // new state for what comes from prepayments
		setPaidPrepaymentsTotal(totalPrepaymentsAvailable.toFixed(2));
		setFinalInvoiceAmount(invoiceAmount > 0 ? invoiceAmount : '0.00');
		setShowGenerateInvoiceModal(true);
	};

	// Helper function to determine if prepayments can fully cover the invoice
	const canPrepaymentsCoverInvoice = () => {
		const tasksTotal = parseFloat(unbilledTasksTotal) || 0;
		const availablePrepayments = parseFloat(paidPrepaymentsTotal) || 0;
		return availablePrepayments >= tasksTotal;
	};

	const handleConfirmGenerateInvoice = async (sendEmail) => {
		try {
			setIsInvoiceProcessing(true);
			const unbilledTasks = project.related_tasks.filter((t) => !t.task_billed);
			
			// Check if there are unbilled tasks instead of checking amount
			if (unbilledTasks.length === 0) {
				window.nError({title: t('common.errorTitle'), text: t('project.task.noUnbilledToInvoice'), icon: 'fa-exclamation-triangle'});
				setShowGenerateInvoiceModal(false);
				return;
			}
			const tasksDetails  = unbilledTasks
				.sort((a, b) => {
					// Sort by date ascending (oldest first)
					const dateA = new Date(a.task_date || 0);
					const dateB = new Date(b.task_date || 0);
					return dateA - dateB;
				})
				.map((taskItem) => {
					const minutes   = parseFloat(taskItem.task_duration) || 0;
					const hours     = minutes / 60; // Use precise hours for calculation
					const cost      = (hours * getEffectiveHourlyRate()).toFixed(2); // Round only the final cost
					return {
						taskName: taskItem.task_name,
						date: taskItem.task_date,
						hours: hours.toFixed(2), // Round hours only for display
						cost,
					};
				});
			
			const vatPercentage = getVatPercentage();
			const prepaymentApplied = prepaymentAmountBilled;
			
			// Calculate amounts with precise numbers, only round at the end
			const amountTotal = parseFloat(finalInvoiceAmount);
			const amountNet = amountTotal / (1 + vatPercentage);
			const vatAmount = amountTotal - amountNet;
			
			// Collect prepayment details BEFORE invoice creation
			const prepaymentDetails = [];
			if (prepaymentApplied > 0) {
				const unbilledPrepayments = prepayments.filter((p) => p.status === 'paid' && getPrepaymentAvailable(p) > 0);
				let remainingToBill = parseFloat(prepaymentApplied);
				
				// Calculate which prepayments will be used and how much from each
				for (const prepayment of unbilledPrepayments) {
					if (remainingToBill <= 0) break;
					
					const prevBilled = parseFloat(prepayment.amountBilled || 0);
					const prepaymentTotal = parseFloat(prepayment.amount_total || 0);
					const available = prepaymentTotal - prevBilled;
					
					if (available <= 0) continue;
					
					const applyAmount = Math.min(available, remainingToBill);
					
					if (applyAmount > 0) {
						prepaymentDetails.push({
							prepaymentInvoiceNumber: prepayment.invoiceNumber, // Use actual invoice number
							amount: applyAmount,
							related_project_no: prepayment.related_project_no // Include project number for retainer updates
						});
						remainingToBill -= applyAmount;
					}
				}
			}
			
			const response = await axios.post('/invoices/create', {
				client_name: getRepresentativeName(),
				client_email: client?.email || project?.client_email || '',
				client_type: project.client_type === 'business' ? 'business' : 'individual', // Map client types: person->individual, business->business
				client_pid: client?.pid || project?.client_pid || '',
				// Add business fields if client is a business
				...(project.client_type === 'business' && {
					business_name: client?.business_name || project?.business_name || '',
					business_address: client?.business_address || project?.business_address || '',
					business_reg_code: client?.business_reg_code || project?.business_reg_code || '',
				}),
				service: project.project_name,
				amount_total: amountTotal.toFixed(2),
				amount_net: amountNet.toFixed(2),
				vat: vatAmount.toFixed(2),
				type: 'invoice',
				related_project_id: project._id,
				related_project_no: project.project_no,
				client_id: project.related_client_id,
				prepaymentApplied : prepaymentApplied,
				prepaymentDetails: prepaymentDetails, // Send detailed prepayment information with invoice numbers
				related_partner_id: owner.user_no,
				partner_company_name: owner.partner_company_name,
				partner_name: owner.firstName + ' ' + owner.lastName,
				tasks: tasksDetails, // ???
				status: 'draft',
			});
			
			const invoiceNumber = response.invoice?.invoiceNumber || response.invoiceNumber;
			
			if (!invoiceNumber) {
				window.nError({title: 'System Error', text: 'Error generating invoice in system.', icon: 'fa-exclamation-triangle'});
				setShowGenerateInvoiceModal(false);
				return;
			}
			
			const updatedUnbilledTasks = unbilledTasks.map((taskItem) => {
				const minutes = parseFloat(taskItem.task_duration) || 0;
				const hours = minutes / 60;
				return {
					...taskItem,
					task_billed: true,
					billed_amount: hours * getEffectiveHourlyRate(),  // i dont think its in the system
					related_invoice: invoiceNumber,
				};
			});
			
			const updatedAllTasks = project.related_tasks.map((t) => {
				// Do not change tasks that are already billed and have a related invoice
				if (t.task_billed && t.related_invoice) return t;
				const updated = updatedUnbilledTasks.find((u) => u.task_name === t.task_name);
				return updated || t;
			});
			
			await axios.patch(`/projects/${project._id}`, {
				related_tasks: updatedAllTasks,
			});
			
			setProject((prev) => ({ ...prev, related_tasks: updatedAllTasks }));
			
			const unbilledPrepayments = prepayments.filter((p) => p.status === 'paid' && getPrepaymentAvailable(p) > 0);
			
			let remainingToBill = parseFloat(prepaymentAmountBilled || 0);
			
			// Go through each unbilled prepayment and allocate amount accordingly
		
		await Promise.all(
			unbilledPrepayments.map((p) => {
				const prevBilled = parseFloat(p.amountBilled || 0);
				const prepaymentTotal = parseFloat(p.amount_total || 0);
				const available = prepaymentTotal - prevBilled;
				
				if (available <= 0 || remainingToBill <= 0) {
					return Promise.resolve(); // skip
				}
				
				const applyAmount = Math.min(available, remainingToBill);
				const newAmountBilled = prevBilled + applyAmount;
				const fullyBilled = parseFloat(newAmountBilled.toFixed(2)) >= parseFloat(prepaymentTotal.toFixed(2));
				
				remainingToBill -= applyAmount;
				
				return axios.put(`/invoices/${p._id}`, {
					amountBilled: parseFloat(newAmountBilled.toFixed(2)),
					billed: fullyBilled
				});
			})
		);
		
		const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
		const updatedPrepayments = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'prepayment')
			: [];
		const updatedCreditInvoices = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
			
		setPrepayments(updatedPrepayments);
		setCreditInvoices(updatedCreditInvoices);
			
		/* If prepayment covers invoice then set status paid */
		
		const status = parseFloat(prepaymentAmountBilled) === parseFloat(finalInvoiceAmount) ? 'paid' : 'issued'; 
		const issue_date = new Date();
		const due_date   = new Date(issue_date.getTime() + invoiceDueDays * 24 * 60 * 60 * 1000);
		
		// Prepare update data
		const updateData = {
			status,
			issue_date,
			due_date,
		};
			
			// If invoice is fully paid by prepayments, set receipt_date to current date
			if (status === 'paid') {
				updateData.receipt_date = new Date();
			}
			
			// If prepayments are applied but invoice is NOT fully paid, set prepayments_applied_date
			if (parseFloat(prepaymentAmountBilled) > 0 && status !== 'paid') {
				updateData.prepayments_applied_date = new Date();
			}
			
			await axios.put(
				`/invoices/${response._id}`,
				updateData
			);
			
		if(sendEmail){
			/* Send invoice to client email */
			
			/* OLD: localhost - Commented out: sending all data in link */
			/* const botId = 'botId67af398d24fea1179c8ae2f7'; 
			const paymentLink = `http://localhost:3001/?bot=${botId}&project_no=${project.project_no}&service=${encodeURIComponent(
				project.project_name
			)}&selected_service_price=${finalInvoiceAmount}&invoice_no=${invoiceNumber}`; */
			
			/* OLD: LIVE - Commented out: sending all data in link */
			/* const dueamount = finalInvoiceAmount - paidPrepaymentsTotal;
			const botId = '67cc2842c7a21d8e097693d7';
			const paymentLink = `https://app.hugo.legal/hugoapp?bot=${botId}&project_no=${project.project_no}&service=${encodeURIComponent(
				project.project_name
			)}&selected_service_price=${dueamount}&invoice_no=${invoiceNumber}&invoice_type=invoice&partner_name=${owner.firstName + ' ' + owner.lastName}&partner_email=${owner.email}&client_type=${project.client_type || 'person'}`; */
			
			/* NEW: ID-based solution - only invoice _id in URL */
			const paymentLink = buildInvoicePaymentLink(response._id);
			
			/* NEW: localhost */
			/* const botId = 'botId67af398d24fea1179c8ae2f7';
			const paymentLink = `http://localhost:3001/?bot=${botId}&invoice_id=${response._id}`; */
			
			const status = parseFloat(prepaymentAmountBilled) === parseFloat(finalInvoiceAmount) ? 'paid' : 'sent';
			const issue_date = new Date();
			const due_date = new Date(issue_date.getTime() + invoiceDueDays * 24 * 60 * 60 * 1000);
				
				const sendInvoice = await axios.post(`/projects/${project._id}/sendInvoice`, {
					email: client?.email || project?.client_email || '',
					client: getRepresentativeName(),
					client_type: project.client_type,
					business_name: client?.business_name || project?.business_name || '',
					amount: finalInvoiceAmount,
					prepayments_amount: prepaymentAmountBilled,
					service: project.project_name,
					invoice_no: invoiceNumber,
					invoice_id: response._id,
					owner: project.project_owner,
					partner_name: owner.partner_name,
					partner_company: owner.partner_company_name,
					partner_company_reg_id: owner.partner_company_reg_id,
					partner_company_address: owner.partner_company_address,
					partner_hourly_rate: getEffectiveHourlyRate(),
					link: paymentLink,
					tasksDetails: tasksDetails,
					vat: vatAmount,
					due_date,
					status,
				});
				
				if (sendInvoice.error) {
					setInvoiceFeedback(`Invoice ${invoiceNumber} EMAIL NOT SENT`);
					return;
				}
				
			/* If prepayment covers invoice then set status paid */
			
			await axios.put(`/invoices/${response._id}`, {
				status,
				issue_date,
				due_date,
			});
			}
			
		const updatedInvoiceResponse = await axios.get(`/invoices/project/${project._id}`);
		const regularInvoices = Array.isArray(updatedInvoiceResponse)
			? updatedInvoiceResponse.filter((inv) => inv.type === 'invoice')
			: [];
		const updatedCreditInvoices2 = Array.isArray(updatedInvoiceResponse)
			? updatedInvoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
				
		setInvoices(regularInvoices);
		setCreditInvoices(updatedCreditInvoices2);
			
			setInvoiceFeedback(`Invoice ${invoiceNumber} generated successfully.`);
			window.nSuccess({title: 'Success!', text: 'Invoice generated successfully!', icon: 'fa-check-square'});
		} catch (error) {
			logger.error('Error generating invoice:', error);
			window.nError({title: 'Error!', text: 'Error generating invoice', icon: 'fa-exclamation-triangle'});
		} finally {
			setIsInvoiceProcessing(false);
			setShowGenerateInvoiceModal(false);
		}
	};
	
	// REPLY SOLUTION: to be removed from code in future.
	// Send Initial Response Handler, draft save, modal handlers — entire block commented out below.
	/*
	const handleSendInitialResponse = async () => {
		try {
			if (!initialResponseMessage.trim()) {
				window.nError({
					title: 'Validation Error',
					text: 'Please enter a message.',
					icon: 'fa-exclamation-triangle'
				});
				return;
			}
			
			setIsSendingInitialResponse(true);
			
			const clientEmail = client?.email || project?.client_email;
			if (!clientEmail) {
				window.nError({
					title: 'Error',
					text: 'Client email not found.',
					icon: 'fa-exclamation-triangle'
				});
				setIsSendingInitialResponse(false);
				return;
			}
			
			const response = await axios.post(`/projects/${project._id}/sendInitialResponse`, {
				subject: initialResponseSubject,
				message: initialResponseMessage,
				client_email: clientEmail
			});
			
			if (response.error) {
				window.nError({
					title: 'Error',
					text: 'Failed to send initial response. Please try again.',
					icon: 'fa-exclamation-triangle'
				});
				return;
			}
			
			window.nSuccess({
				title: 'Success',
				text: 'Initial response sent successfully!',
				icon: 'fa-check-circle'
			});
			
			setInitialResponseMessage('');
			await refreshProject();
			
		} catch (error) {
			logger.error('Error sending initial response:', error);
			window.nError({
				title: 'Error',
				text: error.response?.data?.message || 'Failed to send initial response. Please try again.',
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setIsSendingInitialResponse(false);
			setShowInitialResponseModal(false);
		}
	};
	
	const handleSaveDraft = async (showNotification = true, messageToSave = null) => {
		try {
			if (!project?._id) {
				return;
			}
			
			const message = messageToSave !== null ? messageToSave : initialResponseMessage;
			setIsSavingDraft(true);
			
			await axios.post(`/projects/${project._id}/saveInitialResponseDraft`, {
				message: message
			});
			
			if (showNotification) {
				window.nSuccess({
					title: 'Saved',
					text: 'Draft saved successfully.',
					icon: 'fa-check-circle'
				});
			}
			
			setProject(prev => ({
				...prev,
				initial_response_draft: {
					message: message,
					updated_at: new Date().toISOString()
				}
			}));
			
		} catch (error) {
			logger.error('Error saving draft:', error);
			if (showNotification) {
				window.nError({
					title: 'Error',
					text: 'Failed to save draft.',
					icon: 'fa-exclamation-triangle'
				});
			}
		} finally {
			setIsSavingDraft(false);
		}
	};
	
	const handleInitialResponseChange = (data) => {
		setInitialResponseMessage(data);
		if (autoSaveTimeout) {
			clearTimeout(autoSaveTimeout);
		}
		const timeout = setTimeout(() => {
			if (data.trim() && project?._id) {
				handleSaveDraft(false, data);
			}
		}, 2000);
		setAutoSaveTimeout(timeout);
	};
	
	const openInitialResponseModal = () => {
		if (project?.initial_response_draft?.message) {
			setInitialResponseMessage(project.initial_response_draft.message);
		} else {
			setInitialResponseMessage('');
		}
		setShowInitialResponseModal(true);
	};
	
	const closeInitialResponseModal = () => {
		if (autoSaveTimeout) {
			clearTimeout(autoSaveTimeout);
		}
		setShowInitialResponseModal(false);
	};
	
	const handleShowSendConfirmation = async () => {
		const lawyerEmail = currentUser?.email || owner?.email || '';
		setFromEmailForConfirmation(lawyerEmail);
		setShowSendConfirmationModal(true);
	};
	
	const handleConfirmSend = async () => {
		setShowSendConfirmationModal(false);
		await handleSendInitialResponse();
	};
	*/
	
	/* Used to send or resend invoice from table */
	
	const handleSendInvoice = async (invoice) => {
		try {
			setIsInvoiceProcessing(true);
		const relatedTasks = project.related_tasks.filter((taskItem) => taskItem.related_invoice === invoice.invoiceNumber);
		const tasksDetails = relatedTasks
			.sort((a, b) => {
				// Sort by date ascending (oldest first)
				const dateA = new Date(a.task_date || 0);
				const dateB = new Date(b.task_date || 0);
				return dateA - dateB;
			})
			.map((taskItem) => {
				const minutes = parseFloat(taskItem.task_duration) || 0;
				const hours   = minutes / 60; // Use precise hours for calculation
				const cost    = (hours * getEffectiveHourlyRate()).toFixed(2); // Round only the final cost
				return {
					taskName: taskItem.task_name,
					date: taskItem.task_date,
					hours: hours.toFixed(2), // Round hours only for display
					cost,
				};
			});
			
		const comment = prepaymentComment || '';
		
		/* OLD: LIVE - Commented out: sending all data in link */
		/* const dueamount = invoice.amount_total - paidPrepaymentsTotal;
		const botId = '67cc2842c7a21d8e097693d7';
		const paymentLink = `https://app.hugo.legal/hugoapp?bot=${botId}&project_no=${project.project_no}&service=${encodeURIComponent(
			project.project_name
		)}&comment=${encodeURIComponent(comment)}&selected_service_price=${dueamount}&invoice_no=${invoice.invoiceNumber}&invoice_type=invoice&partner_name=${owner.firstName + ' ' + owner.lastName}&partner_email=${owner.email}&client_type=${project.client_type || 'person'}`; */
		
		/* OLD: localhost - Commented out: sending all data in link */
		/* const botId = 'botId67af398d24fea1179c8ae2f7';
		const paymentLink = `http://localhost:3001/?bot=${botId}&project_no=${project.project_no}&service=${encodeURIComponent(
			project.project_name
		)}&comment=${encodeURIComponent(comment)}&selected_service_price=${invoice.amount_total}&invoice_no=${invoice.invoiceNumber}&client_type=${project.client_type || 'person'}`; */
		
		/* NEW: ID-based solution - only invoice _id in URL */
		const paymentLink = buildInvoicePaymentLink(invoice._id);
		
		/* NEW: localhost */
		/* const botId = 'botId67af398d24fea1179c8ae2f7';
		const paymentLink = `http://localhost:3001/?bot=${botId}&invoice_id=${invoice._id}`; */
			
			const due_date = invoice.due_date || (() => { const d = new Date(); return new Date(d.getTime() + invoiceDueDays * 24 * 60 * 60 * 1000); })();
			const sendInvoice = await axios.post(`/projects/${project._id}/sendInvoice`, {
				email: project.client_email,
				client: getRepresentativeName(),
				client_type: project.client_type,
				business_name: client?.business_name || project?.business_name || '',
				amount: invoice.amount_total.toFixed(2),
				prepayments_amount: paidPrepaymentsTotal,
				//amountDue: 
				service: project.project_name,
				invoice_no: invoice.invoiceNumber,
				invoice_id: invoice._id,
				owner: project.project_owner,
				partner_hourly_rate: getEffectiveHourlyRate(),
				partner_name: owner.partner_name,
				partner_company: owner.partner_company_name,
				partner_company_reg_id: owner.partner_company_reg_id,
				partner_company_address: owner.partner_company_address,
				link: paymentLink,
				tasksDetails: tasksDetails,
				vat: invoice.vat,
				due_date,
				status: invoice.status || 'sent',
			});
			
		if (sendInvoice.error) {
			setInvoiceFeedback(`Invoice ${invoice.invoiceNumber} EMAIL NOT SENT`);
			const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
			const regularInvoices = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'invoice')
			: [];
			const creditInvoicesData = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
			setInvoices(regularInvoices);
			setCreditInvoices(creditInvoicesData);
			return;
		}
			
			const issue_date = new Date();
			await axios.put(`/invoices/${invoice._id}`, {
				status: 'sent',
				issue_date,
				due_date,
			});
			
		setInvoiceFeedback(`Invoice ${invoice.invoiceNumber} email sent and updated successfully.`);
		
		const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
		const regularInvoices = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'invoice')
			: [];
		const creditInvoicesData = Array.isArray(invoiceResponse)
			? invoiceResponse.filter((inv) => inv.type === 'credit')
			: [];
				
		setInvoices(regularInvoices);
		setCreditInvoices(creditInvoicesData);
		} catch (error) {
			logger.error("Error sending invoice email:", error);
			setInvoiceFeedback("Error sending invoice email: " + error.message);
		} finally {
			setIsInvoiceProcessing(false);
		}
	};
	
	/* Credit Invoice Handler */
	
	const handleCreditInvoice = (originalInvoice) => {
		const invoiceType = originalInvoice.type === 'prepayment' ? 'prepayment' : 'invoice';
		const prepaymentAvailable = originalInvoice.type === 'prepayment'
			? getPrepaymentAvailable(originalInvoice)
			: 0;
		const prepaymentCreditAmount = originalInvoice.type === 'prepayment'
			? (prepaymentAvailable > 0
				? prepaymentAvailable
				: parseFloat(originalInvoice.amount_total) || 0)
			: null;
		const prepaymentMoneyReceived = originalInvoice.type === 'prepayment' && (
			originalInvoice.status === 'paid'
			|| (originalInvoice.payment_transactions || []).some((t) => parseFloat(t.amount) > 0)
		);
		
		// Build conditional confirmation text based on invoice type and payment status
		const confirmationText = originalInvoice.type === 'prepayment'
			? `<span>${originalInvoice.invoiceNumber}</span><br/>${t('project.credit.prepaymentConfirm', {
				amount: prepaymentCreditAmount.toFixed(2),
				email: prepaymentMoneyReceived ? t('project.credit.prepaymentEmailNote') : '',
				status: (originalInvoice.status === 'paid' || originalInvoice.status === 'sent')
					? t('project.credit.prepaymentMarkCredited')
					: '',
			})}`
			: `<span>${originalInvoice.invoiceNumber}</span><br/>This will create a credit note, mark the original as credited, and send credit invoice email and PDF to client.`;
		
		let n; n = window.nConfirm({
			title: `Credit ${invoiceType}?`,
			text: confirmationText,
			icon: 'fa-exclamation-circle',
			onYes: async () => {
				n?.close();
				try {
					setIsInvoiceProcessing(true);
					
					// Call backend to create credit invoice
					const response = await axios.post('/invoices/credit', {
						original_invoice_id: originalInvoice._id
					});
					
					logger.log('Credit invoice creation response:', response);
					
					if (response && response.creditInvoice) {
						// Prepare email data based on invoice type (prepayment vs invoice)
						let emailData = {
							email: project.client_email,
							client: getRepresentativeName(),
							client_type: project.client_type,
							business_name: client?.business_name || project?.business_name || '',
							amount: response.creditInvoice.amount_total,
							owner: project.project_owner,
							invoice_no: response.creditInvoice.invoiceNumber,
							invoice_id: response.creditInvoice._id,
							related_invoice_no: originalInvoice.invoiceNumber,
							partner_company: owner.partner_company_name,
							partner_company_reg_id: owner.partner_company_reg_id,
							partner_company_address: owner.partner_company_address,
							partner_hourly_rate: getEffectiveHourlyRate(),
							vat: response.creditInvoice.vat,
							credit_reason: `Kreeditarve arvele ${originalInvoice.invoiceNumber}`,
							invoice_type: originalInvoice.type // 'invoice' or 'prepayment'
						};
						
						// If credit is from regular invoice, fetch task details
						if (originalInvoice.type === 'invoice') {
							const relatedTasks = project.related_tasks.filter((taskItem) => taskItem.related_invoice === originalInvoice.invoiceNumber);
							const tasksDetails = relatedTasks
								.sort((a, b) => {
									const dateA = new Date(a.task_date || 0);
									const dateB = new Date(b.task_date || 0);
									return dateA - dateB;
								})
								.map((taskItem) => {
									const minutes = parseFloat(taskItem.task_duration) || 0;
									const hours = minutes / 60;
									const cost = (hours * getEffectiveHourlyRate()).toFixed(2);
									return {
										taskName: taskItem.task_name,
										date: taskItem.task_date,
										hours: hours.toFixed(2),
										cost,
									};
								});
							emailData.tasksDetails = tasksDetails;
						}
						// If credit is from prepayment, include prepayment details
						else if (originalInvoice.type === 'prepayment') {
							const creditTotalAbs = Math.abs(parseFloat(response.creditInvoice.amount_total) || 0);
							const origTotalAbs = Math.abs(parseFloat(originalInvoice.amount_total) || 0);
							const isPartialCredit = origTotalAbs > 0 && creditTotalAbs < origTotalAbs - 0.01;
							emailData.payment_description = originalInvoice.payment_description || 'Ettemaks õigusteenuse eest';
							emailData.billable_hours = isPartialCredit ? '1' : (originalInvoice.billable_hours || '1.0');
							emailData.hourly_rate = originalInvoice.hourly_rate || getEffectiveHourlyRate();
						}
						
						const shouldSendCreditEmail = originalInvoice.type === 'invoice' || prepaymentMoneyReceived;
						let emailSent = false;
						if (shouldSendCreditEmail) {
							try {
								await axios.post(`/projects/${project._id}/sendCreditInvoice`, emailData);
								emailSent = true;
							} catch (emailError) {
								logger.error('Error sending credit invoice email:', emailError);
							}
						}
					
					// Build conditional success message based on invoice type
					const successText = originalInvoice.type === 'prepayment'
						? (emailSent
							? t('project.credit.prepaymentSuccessSent', { number: response.creditInvoice.invoiceNumber })
							: t('project.credit.prepaymentSuccessCreated', { number: response.creditInvoice.invoiceNumber }))
						: `Credit note ${response.creditInvoice.invoiceNumber} created and sent successfully!`;
					
					window.nSuccess({
						title: 'Success!',
						text: successText,
						icon: 'fa-check-square'
					});
						
						// Small delay to ensure database is updated
						await new Promise(resolve => setTimeout(resolve, 100));
						
						// Refresh invoices, prepayments, and credit invoices
						const invoiceResponse = await axios.get(`/invoices/project/${project._id}`);
						logger.log('Credit invoice refetch - full response:', invoiceResponse);
						logger.log('Credit invoice refetch - is array?', Array.isArray(invoiceResponse));
						
						const updatedPrepayments = Array.isArray(invoiceResponse)
							? invoiceResponse.filter((inv) => inv.type === 'prepayment')
							: [];
						const regularInvoices = Array.isArray(invoiceResponse)
							? invoiceResponse.filter((inv) => inv.type === 'invoice')
							: [];
						const creditInvoicesData = Array.isArray(invoiceResponse)
							? invoiceResponse.filter((inv) => inv.type === 'credit')
							: [];
						
						logger.log('Credit invoices filtered:', creditInvoicesData);
						logger.log('Setting credit invoices state with:', creditInvoicesData.length, 'items');
						
						setPrepayments(updatedPrepayments);
						setInvoices(regularInvoices);
						setCreditInvoices(creditInvoicesData);

						// Refetch project so task_credited flags appear on tasks
						const projectRefresh = await axios.get(`/projects/${project.project_no}?t=${Date.now()}`);
						setProject((prev) => ({
							...prev,
							...projectRefresh,
							related_tasks: projectRefresh.related_tasks ?? prev.related_tasks,
						}));
					}
				} catch (error) {
					logger.error('Error creating credit invoice:', error);
					window.nError({
						title: 'Error!',
						text: error.response?.data?.error || 'Error creating credit invoice. Please try again.',
						icon: 'fa-exclamation-triangle'
					});
				} finally {
					setIsInvoiceProcessing(false);
				}
			},
			onNo: () => {
				n?.close();
			}
		});
	};
	
	/* Finish Project & Set Active */
	
	const onFinishProject = () => setShowFinishProjectModal(true);
	const handleFinishProject = async (send_feedback) => {
		try {
			await axios.post(`/projects/${project._id}/finish`, {
			project_owner_email: owner.email,
			send_feedback,
			client_email: client?.email || '',
			client_name: getRepresentativeName()
			});
			setProject(prev => ({ ...prev, status: 'finished' }));
			window.nSuccess({
				title: 'Success!', 
				text: "Project finished.",
				icon: 'fa-check-square'
			});
		} catch (err) {
			logger.error(err);
			window.nError({title: 'Error!', text: 'Error finishing project', icon: 'fa-exclamation-triangle'});
		} finally {
			setShowFinishProjectModal(false);
		}
	};

	const handleDeleteProject = () => {
		const confirmText = `<div style="text-align: left; margin-top: 12px; margin-bottom: 16px;"><strong>${project.project_name || ''}</strong><br/>${t('projects.deleteConfirmClient')} ${project.client_name || ''}</div>`;
		let n; n = window.nConfirm({
			title: t('projects.deleteConfirm'),
			text: confirmText,
			icon: 'fa-exclamation-circle',
			onYes: async () => {
				try {
					await axios.delete('/projects/bulk', { projectIds: [project._id] });
					n?.close();
					setShowActionMenu(false);
					window.nSuccess({ title: t('common.successTitle'), text: t('project.deleteSuccess'), icon: 'fa-check' });
					history.push('/projects');
				} catch (err) {
					logger.error(err);
					window.nError({ title: t('common.errorTitle'), text: t('project.deleteError'), icon: 'fa-exclamation-triangle' });
					n?.close();
				}
			},
			onNo: () => {
				n?.close();
				setShowActionMenu(false);
			},
		});
	};

	const handleSetCancelled = () => {
		// If admin, use simple confirmation tooltip
		if (currentUser?.role === 'admin') {
			let n; n = window.nConfirm({
				title: 'Cancel Project',
				text: `Are you sure you want to cancel project #${project?.project_no}?`,
				icon: 'fa-exclamation-circle',
				onYes: async () => {
					await handleAdminCancel();
					n?.close();
					setShowActionMenu(false);
				},
				onNo: () => {
					n?.close();
					setShowActionMenu(false);
				}
			});
		} else {
			// If lawyer, show modal with reason requirement
			setCancelReason('');
			setCancelReasonError('');
			setShowCancelProjectModal(true);
			setShowActionMenu(false);
		}
	};
	
	/* File Upload Handlers */
	
	const handleFileUpload = async (files) => {
		if (!files || files.length === 0) return;
		
		if (!owner || !owner.email) {
			window.nError({
				title: 'Error!',
				text: 'Cannot upload files: Project owner not found',
				icon: 'fa-exclamation-triangle'
			});
			return;
		}
		
		setUploadError('');
		
		try {
			const formData = new FormData();
			formData.append('message', '');
			for (const file of files) {
				formData.append('files', file);
			}
			formData.append('ownerEmail', owner.email);
			
			await axios.post(`/projects/${project._id}/uploadAndSendMessage`, formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
			
			await refreshProject();
			
			window.nSuccess({
				title: 'Success!',
				text: `${files.length} file(s) uploaded successfully`,
				icon: 'fa-check-square'
			});
		} catch (error) {
			logger.error('File upload error:', error);
			const errorMsg = error.message || 'Error uploading files. Please try again.';
			setUploadError(errorMsg);
			window.nError({
				title: 'Error!',
				text: errorMsg,
				icon: 'fa-exclamation-triangle'
			});
		}
	};
	
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!isDraggingOver) {
			setIsDraggingOver(true);
		}
	};
	
	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		// Only set to false if we're leaving the container entirely
		if (e.currentTarget.contains(e.relatedTarget)) {
			return;
		}
		setIsDraggingOver(false);
	};
	
	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingOver(false);
		
		if (!canUploadProjectFiles) {
			window.nError({
				title: 'Error!',
				text: !owner?.email
					? 'Cannot upload files: Project owner must be assigned first'
					: 'Cannot upload files: Please start the project first',
				icon: 'fa-exclamation-triangle'
			});
			return;
		}
		
		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			handleFileUpload(files);
		}
	};
	
	const handleAdminCancel = async () => {
		try {
			setIsCancelling(true);
			await axios.post(`/projects/${project._id}/cancel`, { 
				reason: 'Cancelled by admin'
			});
			await refreshProject();
			window.nSuccess({
				title: 'Success!', 
				text: 'Project cancelled successfully.', 
				icon: 'fa-check-square'
			});
		} catch (error) {
			logger.error('Error cancelling project:', error);
			window.nError({
				title: 'Error!', 
				text: 'Error cancelling project. Please try again.', 
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setIsCancelling(false);
		}
	};

	const handleConfirmCancel = async (e) => {
		e.preventDefault();
		
		// Validate reason
		if (!cancelReason.trim()) {
			setCancelReasonError(t('project.declineReasonRequired'));
			return;
		}
		
		if (cancelReason.trim().length < 10) {
			setCancelReasonError(t('project.declineReasonTooShort'));
			return;
		}
		
		try {
			setIsCancelling(true);
			await axios.post(`/projects/${project._id}/cancel`, { 
				reason: cancelReason.trim()
			});
			setShowCancelProjectModal(false);
			setCancelReason('');
			await refreshProject();
			window.nSuccess({
				title: 'Success!', 
				text: t('project.declineSuccess'), 
				icon: 'fa-check-square'
			});
		} catch (error) {
			logger.error('Error declining project:', error);
			window.nError({
				title: 'Error!', 
				text: t('project.declineError'), 
				icon: 'fa-exclamation-triangle'
			});
		} finally {
			setIsCancelling(false);
		}
	};
	const handleSetActive = async () => {
		logger.log('[handleSetActive] Starting, project:', project?.project_no, 'type:', project?.project_type, 'status:', project?.status);
		
		try {
			// Check if this is an inquiry-type project
			const isInquiry = project?.project_type && /^inquiry$/i.test(project.project_type);
			
			if (isInquiry) {
				// For inquiry projects, use the claim endpoint
				logger.log('[handleSetActive] Attempting to claim inquiry for project ID:', project._id);
				const claimResponse = await axios.post(`/projects/${project._id}/claim-prospect`);
				
				logger.log('[handleSetActive] Claim response:', claimResponse);
				
				// The axios wrapper returns errors as data objects with a 'status' field
				// Check if it's an error response (400 = already claimed, etc.)
				if (claimResponse?.status === 400 || claimResponse?.status === 403) {
					logger.log('[handleSetActive] Error response from backend:', claimResponse.status);
					
					// Check if it's because someone else already claimed it
					if (claimResponse.alreadyClaimed) {
						logger.log('[handleSetActive] Already claimed by someone else');
						setAccessError(claimResponse.message || 'This legal inquiry is already acquired by another lawyer just moments before. Please select another.');
						return;
					}
					
					// Some other error
					logger.log('[handleSetActive] Error during claim');
					window.nError({
						title: 'Error!',
						text: claimResponse.message || 'Unable to claim this inquiry.',
						icon: 'fa-exclamation-triangle'
					});
					return;
				} else if (claimResponse?.alreadyOwned) {
					// User already owns this inquiry, just refresh
					logger.log('[handleSetActive] User already owns this inquiry, refreshing...');
					justClaimedRef.current = true;
					refreshProject();
					return;
				} else if (claimResponse?.project) {
					// Successful claim - response contains project data
					logger.log('[handleSetActive] Claim successful!');
					
					// Set flag to indicate we just claimed this project
					justClaimedRef.current = true;
					
					// Success - show success message and refresh
					window.nSuccess({
						title: 'Success!',
						text: 'Inquiry claimed! You are now the project owner.',
						icon: 'fa-check-square'
					});
					
					// Refresh project data after a brief delay to let backend commit
					setTimeout(() => {
						refreshProject();
					}, 500);
					return;
				}
			} else {
				// Regular project activation (non-inquiry)
				logger.log('[handleSetActive] Regular project activation');
				await axios.patch(`/projects/${project._id}`, { status: 'active' });
				
				// Refresh project
				refreshProject();
			}
			
		} catch (error) {
			logger.error('[handleSetActive] Final error:', error);
			
			const errorMessage = error?.response?.data?.message || 'Error starting project. Please try again.';
			window.nError({
				title: 'Error!', 
				text: errorMessage, 
				icon: 'fa-exclamation-triangle'
			});
		}
	};

	/* Custom Hourly Rate Functions */
	
	const openHourlyRateModal = () => {
		setCustomHourlyRate(project.project_hourly_rate ? project.project_hourly_rate.toString() : '');
		setShowHourlyRateModal(true);
	};

	const handleSetHourlyRate = async () => {
		try {
			setHourlyRateLoading(true);
			const rate = customHourlyRate.trim() === '' ? null : parseFloat(customHourlyRate);
			
			if (rate !== null && (isNaN(rate) || rate < 0)) {
				window.nError({title: 'Validation Error', text: 'Please enter a valid positive number', icon: 'fa-exclamation-triangle'});
				return;
			}

			await axios.patch(`/projects/${project._id}/set-hourly-rate`, {
				hourly_rate: rate
			});
			
			// Refresh the project to show updated rate
			await refreshProject();
			setShowHourlyRateModal(false);
			setCustomHourlyRate('');
			window.nSuccess({
				title: 'Success!', 
				text: rate ? 'Custom hourly rate set successfully!' : 'Hourly rate reset to default successfully!', 
				icon: 'fa-check-square'
			});
		} catch (error) {
			logger.error('Error setting hourly rate:', error);
			window.nError({title: 'Error!', text: 'Error setting hourly rate. Please try again.', icon: 'fa-exclamation-triangle'});
		} finally {
			setHourlyRateLoading(false);
		}
	};

	/* Edit Project Functions */
	
	const openEditProjectModal = () => {
		setEditProjectForm({
			project_name: project.project_name || '',
			project_description: project.project_description || '',
			project_type: project.project_type === 'general' ? 'General Booking' : (project.project_type || 'General Booking')
		});
		setShowEditProjectModal(true);
	};

	const handleUpdateProject = async (e) => {
		e.preventDefault();
		try {
			await axios.patch(`/projects/${project._id}`, editProjectForm);
			await refreshProject();
			setShowEditProjectModal(false);
			window.nSuccess({
				title: 'Success!', 
				text: 'Project updated successfully!', 
				icon: 'fa-check-square'
			});
		} catch (error) {
			logger.error('Error updating project:', error);
			window.nError({title: 'Error!', text: 'Error updating project. Please try again.', icon: 'fa-exclamation-triangle'});
		}
	};

	const handleProjectTitleSave = async (newTitle) => {
		const trimmed = String(newTitle || '').trim();
		if (!trimmed || !project?._id) {
			return false;
		}
		if (trimmed === (project.project_name || '')) {
			return true;
		}
		try {
			await axios.patch(`/projects/${project._id}`, { project_name: trimmed });
			setProject((prev) => ({ ...prev, project_name: trimmed }));
			return true;
		} catch (error) {
			logger.error('Error updating project title:', error);
			window.nError({
				title: t('common.errorTitle'),
				text: t('projects.updateError'),
				icon: 'fa-exclamation-triangle',
			});
			return false;
		}
	};

	const handleLegalFieldIdsSave = async (legalFieldIds) => {
		const previous = {
			project_tag: project.project_tag,
			legal_field_ids: project.legal_field_ids,
		};
		setProject((prev) => ({ ...prev, legal_field_ids: legalFieldIds }));
		const response = await axios.patch(`/projects/${project._id}`, { legal_field_ids: legalFieldIds });
		if (response?.status && response.status >= 400) {
			setProject((prev) => ({ ...prev, ...previous }));
			logger.error('Error updating legal field:', response);
			window.nError({
				title: 'Error!',
				text: response.message || t('project.legalFieldUpdateError'),
				icon: 'fa-exclamation-triangle',
			});
			throw new Error(response.message || 'Legal field update failed');
		}
		if (response) {
			setProject((prev) => ({
				...prev,
				legal_field_ids: response.legal_field_ids ?? legalFieldIds,
				project_tag: response.project_tag ?? prev.project_tag,
			}));
		}
	};

	const handleClassifyFields = async () => {
		setClassifyError(null);
		setFieldProposal(null);
		const descriptionText = stripHtmlTags(project.project_description || '').trim();
		if (!descriptionText) {
			setClassifyError(t('project.classifyFieldsEmpty'));
			return;
		}
		setClassifyingFields(true);
		try {
			const response = await axios.post(`/projects/${project._id}/classify-fields`);
			if (response?.status && response.status >= 400) {
				setClassifyError(response.message || t('project.classifyFieldsError'));
				return;
			}
			setFieldProposal({
				results: response?.results || [],
				selectedFields: response?.selectedFields || [],
				droppedFields: response?.droppedFields || [],
				profileName: response?.profileName || '',
				classifierFieldCount: response?.classifierFieldCount ?? 1,
			});
		} catch (err) {
			logger.error('Error classifying legal fields:', err);
			setClassifyError(t('project.classifyFieldsError'));
		} finally {
			setClassifyingFields(false);
		}
	};

	const handleApplyFieldProposal = async () => {
		if (!fieldProposal) return;
		const { selectedFields = [], results = [], classifierFieldCount = 1 } = fieldProposal;
		const toApply = selectedFields.length > 0
			? selectedFields
			: results.slice(0, Math.max(1, Math.min(3, Number(classifierFieldCount) || 1)));
		const ids = toApply.map((field) => Number(field.field_id)).filter(Number.isFinite);
		if (!ids.length) return;
		await handleLegalFieldIdsSave(ids);
		setFieldProposal(null);
		setClassifyError(null);
	};
	
	/* Owner Change Functions */
	
	const prefillReassignHourlyRate = (ownerId, lawyersList) => {
		const lawyer = (lawyersList || []).find((l) => String(l.user_no) === String(ownerId));
		if (lawyer?.hourlyRate != null && Number(lawyer.hourlyRate) > 0) {
			setReassignHourlyRate(String(lawyer.hourlyRate));
		} else {
			setReassignHourlyRate('');
		}
	};

	const openChangeOwnerModal = async () => {
		try {
			setOwnerChangeLoading(true);
			const response = await axios.get(`/projects/${project._id}/match-lawyers`);
			const lawyers = response?.lawyers || [];
			const ownerId = project.project_owner_id || '';
			setMatchLawyers(lawyers);
			setMatchCriteria(response?.criteria || null);
			setSelectedNewOwnerId(ownerId);
			prefillReassignHourlyRate(ownerId, lawyers);
			/* REPLY SOLUTION: to be removed from code in future.
			setClearInitialResponse(false);
			setRestoreReplyTaskIndex(null);
			*/
			setOwnerChangeFeedback([]);
			setShowChangeOwnerModal(true);
		} catch (error) {
			logger.error('Error fetching match lawyers:', error);
			window.nError({title: 'Error!', text: t('project.matchLawyersLoadError'), icon: 'fa-exclamation-triangle'});
		} finally {
			setOwnerChangeLoading(false);
		}
	};

	const handleChangeOwner = async () => {
		if (!selectedNewOwnerId) {
			window.nError({title: 'Validation Error', text: 'Please select a new owner', icon: 'fa-exclamation-triangle'});
			return;
		}

		if (selectedNewOwnerId === project.project_owner_id) {
			window.nError({title: 'Validation Error', text: 'Selected user is already the owner', icon: 'fa-exclamation-triangle'});
			return;
		}

		const patchBody = {
			new_owner_id: selectedNewOwnerId,
			/* REPLY SOLUTION: to be removed from code in future.
			clear_initial_response: clearInitialResponse,
			restore_reply_task_index: restoreReplyTaskIndex
			*/
		};
		if (reassignHourlyRate.trim() !== '') {
			const parsedRate = parseFloat(reassignHourlyRate);
			if (isNaN(parsedRate) || parsedRate < 0) {
				window.nError({title: 'Validation Error', text: 'Please enter a valid positive hourly rate', icon: 'fa-exclamation-triangle'});
				return;
			}
			patchBody.hourly_rate = parsedRate;
		}

		try {
			setOwnerChangeLoading(true);
			setOwnerChangeFeedback([t('project.ownerChangeReassigning')]);

			const response = await axios.patch(`/projects/${project._id}/change-owner`, patchBody);

			const messages = [t('project.ownerChangeSuccess')];
			/* REPLY SOLUTION: to be removed from code in future.
			if (clearInitialResponse) messages.push(t('project.ownerChangeCleared'));
			if (restoreReplyTaskIndex !== null) messages.push(t('project.ownerChangeReplyRestored'));
			*/

			// Refresh the project to show updated owner
			await refreshProject();

			const emailNotification = response?.emailNotification;
			if (emailNotification?.sent) {
				messages.push(t('project.ownerChangeEmailSent', { recipient: emailNotification.recipient }));
			} else if (emailNotification?.attempted) {
				messages.push(t('project.ownerChangeEmailFailed', { error: emailNotification.error || 'Unknown error' }));
			} else if (emailNotification) {
				messages.push(t('project.ownerChangeNoEmail'));
			}

			messages.push(t('project.ownerChangeDone'));
			setOwnerChangeFeedback(messages);
		} catch (error) {
			logger.error('Error changing project owner:', error);
			setOwnerChangeFeedback([t('project.ownerChangeError', { message: error?.response?.data?.message || 'Could not change owner. Please try again.' })]);
		} finally {
			setOwnerChangeLoading(false);
		}
	};
	
	// Helper for status icon in simple table
	
	const renderStatusIcon = (status) => {
		
		// Map statuses to colors
		
		let className = 'status status-';
		
		if (status === 'active') className += 'active';
		else if (status === 'waiting') className += 'waiting';
		else if (status === 'finished') className += 'finished';
		else if (status === 'cancelled') className += 'cancelled';
		
		return (
			<div className={className}>
				<span className="indicator"></span>
			</div>
		);
	};

	const getProjectStatusLabel = (status) => {
		switch (status) {
			case 'active':
				return t('project.statusWorking');
			case 'waiting':
				return t('common.statusWaiting');
			case 'finished':
				return t('common.statusFinished');
			case 'cancelled':
				return t('common.statusCancelled');
			case 'done':
				return t('common.statusDone');
			default:
				return status || '';
		}
	};

	const renderProjectSidebarMeta = (placementClass) => {
		const isHeaderMenu = placementClass === 'project-header-meta';
		const statusLabel = (
			<span>
				{t('project.projectStatus')}: {getProjectStatusLabel(project.status)}
			</span>
		);
		const dateRows = (
			<div className="sidebar-meta sidebar-meta--dates">
				<div className="sidebar-meta-row">
					<span className="sidebar-meta-label">{t('project.lastActivity')}:</span>
					<strong>{formatSidebarDate(project.updatedAt)}</strong>
				</div>
				<div className="sidebar-meta-row">
					<span className="sidebar-meta-label">{t('project.createdOn')}:</span>
					<strong>{formatSidebarDate(project.createdAt)}</strong>
				</div>
			</div>
		);

		return (
			<div className={`sidebar-meta ${placementClass || ''}`.trim()}>
				{isHeaderMenu ? (
					<>
						<div className="sidebar-project-status">
							{renderStatusIcon(project.status)}
							{statusLabel}
						</div>
						{dateRows}
					</>
				) : (
					<>
						<div className="sidebar-project-status">
							{renderStatusIcon(project.status)}
							{statusLabel}
						</div>
						{dateRows}
					</>
				)}
			</div>
		);
	};
	
	/* Description Accordion */
	
	const maxChars = 200;
	const description = project.project_description || '';
	// For closed state, strip HTML and use plain text
	const plainTextDesc = stripHtmlTags(description);
	const truncatedDesc = plainTextDesc.length > maxChars ? plainTextDesc.substring(0, maxChars) + '...' : plainTextDesc;
	
	/* Render HTML */
	
	return (
		<div
			id="project"
			className={`row table_v2 project${isSidebarPinned ? ' project--sidebar-pinned' : ''}${
				sidebarDocsExpanded ? ' project--sidebar-docs-expanded' : ''
			}`}
		>
			<div className="col p-0">
				<div className="x_panel chatbot-builder">
					<div className="header-menu d-flex justify-content-between align-items-center">
						<div className="header-title d-flex align-items-center">
							<h1 className="project-header-title">
								<span className="project-header-title-text">
									{t('project.title', { number: project.project_no })}
									{project.project_type && (
										<>
											{' · '}
											<ProjectTypeInfoTooltip
												projectType={project.project_type}
												label={project.project_type}
												t={t}
											/>
										</>
									)}
								</span>
							</h1>
						</div>
						<div className="header-actions d-flex align-items-center">
							{renderProjectSidebarMeta('project-header-meta')}
							{!isProjectCancelled && !isSupport && (
								<>
									{isProjectFinished && (
										<div className="action action-reopen">
											<button
											className = "button button-blue"
											onClick   = {handleSetActive}
											>
												{t('project.reopenProject')}
											</button>
										</div>
									)}
									{currentUser?.role === 'admin' && (
										<div className="action action-classify-fields">
											<LegalFieldAiClassify
												proposal={fieldProposal}
												loading={classifyingFields}
												error={classifyError}
												onClassify={handleClassifyFields}
												onConfirm={handleApplyFieldProposal}
												onDismiss={() => {
													setFieldProposal(null);
													setClassifyError(null);
												}}
												t={t}
											/>
										</div>
									)}
									{currentUser?.role === 'admin' && (
										<div className="action action-reassign">
											<button
												className="button button-light"
												onClick={openChangeOwnerModal}
												disabled={ownerChangeLoading}
											>
												{ownerChangeLoading ? t('common.loading') : t('project.reassignProject')}
											</button>
										</div>
									)}
									{currentUser?.role === 'lawyer' && canCancelProject() && (
										<div className="action action-decline">
											<button
												type="button"
												className="button button-light"
												onClick={handleSetCancelled}
											>
												{t('project.declineProject')}
											</button>
										</div>
									)}
									{isProjectWaiting && !isClaimedByOther && (
										<div className="action action-start">
											<button
											className = "button button-blue"
											onClick   = {handleSetActive}
											>
												{t('project.startProject')}
											</button>
										</div>
									)}
									{isProjectActive && (
										<div 
											className="action action-finish"
											style={{ position: 'relative', display: 'inline-block' }}
											onMouseEnter = {!canFinishProject ? (e) => showHoverTooltip(e, t('project.finishTooltip')) : undefined}
											onMouseLeave = {!canFinishProject ? hideHoverTooltip : undefined}
										>
											<button
											onClick   = {!canFinishProject ? (e) => { e.preventDefault(); e.stopPropagation(); } : onFinishProject}
											disabled  = {!canFinishProject}
											className = {`button button-blue ${!canFinishProject ? 'inactive' : ''}`}
											>
												{t('project.finishProject')}
											</button>
										</div>
									)}
									{currentUser?.role === 'admin' && (
										<div className="action action-more">
											<button
												className={`button toggle${showActionMenu ? ' active' : ''}`}
												onClick={toggleActionMenu}
											>
												...
											</button>
											{showActionMenu && (
												<div className="dropdown-more">
													<button
														className="item"
														onClick={() => { openEditProjectModal(); setShowActionMenu(false); }}
													>
														{t('project.editProject')}
													</button>
													{canCancelProject() && (
														<button
															className="item"
															onClick={() => { handleSetCancelled(); }}
														>
															{t('project.cancelProject')}
														</button>
													)}
													<button
														className="item"
														onClick={handleDeleteProject}
													>
														{t('common.delete')}
													</button>
													{project.related_client_id && owner?._id && (
														<>
															<button
																className="item"
																type="button"
																onClick={() => handleAdminUnlockField('email')}
															>
																{t('project.adminUnlockEmail')}
															</button>
														</>
													)}
												</div>
											)}
										</div>
									)}
								</>
							)}
						</div>
					</div>
					<div className="x_content">
						<div ref={pageContainerRef} className="page-container d-flex flex-column">
							{!isSupport && (
							<div className="page-header d-flex align-items-center justify-content-between">
								<div className="actions">
									<span
									style={{ display: 'inline-block', position: 'relative' }}
									onMouseEnter = {(!isProjectActive && currentUser?.role !== 'admin') ? (e) => showHoverTooltip(e, t('project.startToAddTasks')) : undefined}
									onMouseLeave = {(!isProjectActive && currentUser?.role !== 'admin') ? hideHoverTooltip : undefined}
									>
										<button
										className = {`button ${(!isProjectActive && currentUser?.role !== 'admin') ? 'inactive' : ''}`}
										onClick   = {openAddTaskModal}
										disabled  = {!isProjectActive && currentUser?.role !== 'admin'}
										>
											{t('project.addTask')}
										</button>
									</span>
									<span
									style={{ display: 'inline-block', position: 'relative' }}
									onMouseEnter = {(!isProjectActive && currentUser?.role !== 'admin') ? (e) => showHoverTooltip(e, t('project.startToAskPrepayment')) : undefined}
									onMouseLeave = {(!isProjectActive && currentUser?.role !== 'admin') ? hideHoverTooltip : undefined}
									>
										<button
										className = {`button ${(!isProjectActive && currentUser?.role !== 'admin') ? 'inactive' : ''}`}
										onClick   = {onGeneratePrepayment}
										disabled  = {!isProjectActive && currentUser?.role !== 'admin'}
										>
											{t('project.askPrepayment')}
										</button>
									</span>
									<span
									style={{ display: 'inline-block', position: 'relative' }}
									onMouseEnter = {!canGenerateInvoice ? (e) => {
										let tooltipText = t('project.addFinishedTasksForInvoice');
										/* REPLY SOLUTION: to be removed from code in future.
										if (hasReplyTasks) {
											tooltipText = t('project.replyTasksBeforeInvoice');
										} else if (hasWaitingTasks) {
										*/
										if (hasWaitingTasks) {
											tooltipText = t('project.finishWaitingTasksForInvoice');
										} else if (!hasUnbilledTasks) {
											tooltipText = t('project.allTasksBilled');
										}
										showHoverTooltip(e, tooltipText);
									} : undefined}
									onMouseLeave = {!canGenerateInvoice ? hideHoverTooltip : undefined}
									>
										<button
										className = {`button ${!canGenerateInvoice ? 'inactive' : ''}`}
										onClick   = {!canGenerateInvoice ? (e) => { e.preventDefault(); e.stopPropagation(); } : onGenerateInvoice}
										disabled  = {!canGenerateInvoice}
										>
											{t('project.generateInvoice')}
										</button>
									</span>
								</div>
							</div>
							)}
							<div className="page-content d-flex flex-column">
								{isMobileView && clientPanelOpen && (
									<button
										type="button"
										className="mobile-client-overlay"
										onClick={() => setClientPanelOpen(false)}
										aria-label={t('project.closeDetails')}
									/>
								)}
								<div className="project-info d-flex">
									<div 
										className="description d-flex flex-column"
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
										style={{
											position: 'relative',
											border: isDraggingOver ? '2px dashed #032097' : '2px solid transparent',
											backgroundColor: isDraggingOver ? 'rgba(3, 32, 151, 0.05)' : undefined,
											borderRadius: isDraggingOver ? '8px' : undefined,
											transition: 'all 0.2s ease'
										}}
									>
										{isDraggingOver && (
											<div style={{
												position: 'absolute',
												top: 0,
												left: 0,
												right: 0,
												bottom: 0,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
												backgroundColor: 'rgba(3, 32, 151, 0.1)',
												borderRadius: '8px',
												zIndex: 10,
												pointerEvents: 'none'
											}}>
												<span style={{ 
													fontSize: '18px', 
													fontWeight: 'bold', 
													color: '#032097' 
												}}>
													{t('project.dropFilesToUpload')}
												</span>
											</div>
										)}
										<div className="entry">
											{/* Phase 2.3: client description text hidden — replaced by LawyerProjectConversation thread (see below).
											{descExpanded ? (
												<p style={{ fontSize: '14px', lineHeight: '1.6' }} dangerouslySetInnerHTML={{__html: convertLineBreaksToHtml(description)}}></p>
											) : (
												<p style={{ fontSize: '14px', lineHeight: '1.6' }}>{truncatedDesc}</p>
											)}
											{description.length > maxChars && (
												<button
												className = "button button-text"
												onClick   = {() => setDescExpanded(!descExpanded)}
												>
													{descExpanded ? 'Show less' : 'Show more'}
												</button>
											)}
											*/}
										</div>
										{/*
										<p>
											<a href={project.project_files} target="_blank" rel="noopener noreferrer">
												{project.project_files}
											</a>
										</p>
										*/}

										<LawyerProjectThreadPanel key={project._id}>
											<LawyerProjectConversation
												ref={lawyerProjectThreadRef}
												projectId={project._id}
												projectTitle={project.project_name || ''}
												initialCommunications={project.communications || []}
												initialComposerDraft={project.lawyer_message_draft?.message || ''}
												projectDescription={project.project_description || ''}
												projectCreatedAt={project.createdAt}
												currentUser={currentUser}
												onThreadHeadChange={handleLawyerThreadHeadChange}
												projectFilesUrl={project.project_files || ''}
												onOpenProjectDetails={
													isMobileView ? () => setClientPanelOpen(true) : undefined
												}
												isProjectStarted={!isSupport && (isProjectActive || currentUser?.role === 'admin')}
												ownerEmail={owner?.email || ''}
												onProjectTitleSave={
													!isProjectCancelled && (currentUser?.role === 'lawyer' || currentUser?.role === 'admin')
														? handleProjectTitleSave
														: undefined
												}
											/>
										</LawyerProjectThreadPanel>

									{/* REPLY SOLUTION: to be removed from code in future. Initial Response / email reply UI section removed. */}
									</div>
									<div className="project-sidebar-slot">
									<div className={`client${isMobileView && clientPanelOpen ? ' mobile-open' : ''}`}>
										{isMobileView && (
											<button
												type="button"
												className="mobile-client-panel-close"
												onClick={() => setClientPanelOpen(false)}
												aria-label={t('project.closeDetails')}
											>
												<i className="fa-solid fa-xmark" />
											</button>
										)}
										{isMobileView && renderProjectSidebarMeta('sidebar-meta--mobile-panel')}

										<div className="sidebar-main">
											<div className="sidebar-section sidebar-section--client">
												<div className="sidebar-client-header">
													<div className="sidebar-client-col sidebar-client-col--left">
														<label>{t('project.client')}</label>
														<p>{getClientName()}</p>
													</div>
													{!isProjectWaiting && (
														<div className="sidebar-client-col sidebar-client-col--right">
															<p className="sidebar-client-phone">{client?.phone || project?.client_phone || ''}</p>
														</div>
													)}
												</div>
												{project.related_client_id && !isProjectWaiting && (
													<div className="sidebar-section-actions sidebar-section-actions--end">
														<button
															type="button"
															className="sidebar-link"
															onClick={openClientViewModal}
														>
															{t('project.viewClientData')} ›
														</button>
													</div>
												)}
											</div>

											<div className="tags">
												<ul>
													<li className="sector">
														<LegalFieldPicker
															fieldIds={project.legal_field_ids || []}
															fallbackLabel={project.project_tag || ''}
															onSave={handleLegalFieldIdsSave}
															disabled={currentUser?.role !== 'admin' && currentUser?.role !== 'lawyer'}
															t={t}
														/>
													</li>
												</ul>
											</div>

											{/* Show owner info for admin and support users */}
											{(currentUser?.role === 'admin' || isSupport) && (
												<>
													<div className="name">
														<label>{t('project.owner')}</label>
														<p>{project.project_owner}</p>
													</div>

													<div className="id">
														<label>{t('project.ownerId')}</label>
														<p>{project.project_owner_id}</p>
													</div>
												</>
											)}

											{(currentUser?.role === 'admin' || currentUser?.role === 'lawyer') && (
												<>
													<div className="sidebar-section sidebar-section--rate">
														<div className="sidebar-kv-row">
															<span className="sidebar-kv-label sidebar-kv-label--small">{t('project.hourlyRate')}:</span>
															<span className="sidebar-kv-value">{formatSidebarHourlyRate(getEffectiveHourlyRate())}</span>
														</div>
														{(currentUser?.role === 'admin' || !isProjectWaiting) && (
															<div className="sidebar-section-actions sidebar-section-actions--end">
																<button
																	type="button"
																	className="sidebar-link"
																	onClick={openHourlyRateModal}
																>
																	{t('project.changeHourlyRate')} ›
																</button>
															</div>
														)}
													</div>

													<div className="sidebar-section sidebar-section--financials">
														<h3 className="sidebar-section-title">{t('project.financials')}</h3>
														<div className="sidebar-kv-list">
															<div className="sidebar-kv-row">
																<span className="sidebar-kv-label">{t('project.prepaidBalance')}:</span>
																<span className="sidebar-kv-value">{formatSidebarMoney(project.total_retainer)}</span>
															</div>
															<div className="sidebar-kv-row">
																<span className="sidebar-kv-label">{t('project.invoiceSum')}:</span>
																<span className="sidebar-kv-value">{formatSidebarMoney(computeInvoiceSum(invoices))}</span>
															</div>
															<div className="sidebar-kv-row">
																<span className="sidebar-kv-label">{t('project.unpaid')}:</span>
																<span className={`sidebar-kv-value${Number(project.total_balance) > 0 ? ' sidebar-kv-value--emphasis' : ''}`}>
																	{formatSidebarMoney(project.total_balance)}
																</span>
															</div>
															<div className="sidebar-kv-row sidebar-kv-row--time">
																<span className="sidebar-kv-label">{t('projects.billed')}:</span>
																<span className="sidebar-kv-value">{formatSidebarDuration(project.total_billed_minutes)}</span>
															</div>
															<div className="sidebar-kv-row">
																<span className="sidebar-kv-label">{t('projects.unbilled')}:</span>
																<span className={`sidebar-kv-value${Number(project.total_unbilled_minutes) > 0 ? ' sidebar-kv-value--emphasis' : ''}`}>
																	{formatSidebarDuration(project.total_unbilled_minutes)}
																</span>
															</div>
														</div>
													</div>

													<ProjectDocumentsList
														key={project._id}
														files={project.files || []}
														communications={project.communications || []}
														t={t}
														pinned={isSidebarPinned}
														onExpandedChange={setSidebarDocsExpanded}
													/>
												</>
											)}
										</div>
										{/*  
										Hide project owner info if not admin
										<p>
											<strong>Project Owner:</strong> {project.project_owner}
										</p>
										<p>
											<strong>Project Owner ID:</strong> {project.project_owner_id}
										</p>
										<p>
											<strong>Hourly Rate:</strong> {owner && owner.partner_hourly_rate}
										</p>
										<p>
											<strong>Partner Company Name:</strong>{' '}
											{owner && owner.partner_company_name ? owner.partner_company_name : 'N/A'}
										</p>
										<p>
											<strong>Partner Email:</strong> {owner && owner.email ? owner.email : 'N/A'}
										</p>
										<p>
											<strong>Partner Company Address:</strong>{' '}
											{owner && owner.partner_company_address ? owner.partner_company_address : 'N/A'}
										</p> */}
									</div>
									</div>
								</div>
							<div className="project-data d-flex flex-column">
								{/* Tasks section - only visible when project is active, user is admin, or project is finished and user is lawyer */}
								{(isProjectActive || currentUser?.role === 'admin' || (isProjectFinished && currentUser?.role === 'lawyer')) && (
								<div className="tasks">
									<div className="title">
										<h2>{t('project.tasks')}</h2>
										{!isSupport && (
										<span
										style={{ display: 'inline-block', position: 'relative' }}
										onMouseEnter = {(!isProjectActive && currentUser?.role !== 'admin') ? (e) => showHoverTooltip(e, t('project.startToAddTasks')) : undefined}
										onMouseLeave = {(!isProjectActive && currentUser?.role !== 'admin') ? hideHoverTooltip : undefined}
										>
											<button
											onClick   = {openAddTaskModal}
											className = {`button button-text ${(!isProjectActive && currentUser?.role !== 'admin') ? 'inactive' : ''}`}
											disabled  = {!isProjectActive && currentUser?.role !== 'admin'}
											>
												{t('project.addTask')} <InlineSvgIcon width={24} className="svg-icon" src="/svg/icon/info.svg" aria-label={t('common.info')} />
											</button>
										</span>
										)}
									</div>
									<div className="list nopaginate">
										{project.related_tasks.length > 0 ? (
											<DataTablePaginated
											data    = {[...project.related_tasks].sort((a, b) => {
												const dateA = new Date(a.task_date || 0);
												const dateB = new Date(b.task_date || 0);
												return dateA - dateB; // Sort oldest first
											})}
											columns = {taskColumns}
											rowClasses = {(row) => {
												return row.status !== 'done' ? 'invoice-unpaid' : '';
											}}
											/>
										) : (
											<>
											<label>{t('project.noTasks')}</label>
											<p>{t('project.noTasksHint')}</p>
											</>
										)}
									</div>
								</div>
								)}
								
								{/* Prepayments section - visible to all users, but add button restricted for lawyers when project not started */}
								<div className="prepayments">
									<div className="title">
										<h2>{t('project.prepayments')}</h2>
										{!isSupport && (
										<span
										style={{ display: 'inline-block', position: 'relative' }}
										onMouseEnter = {(!isProjectActive && currentUser?.role !== 'admin') ? (e) => showHoverTooltip(e, t('project.startToAskPrepayment')) : undefined}
										onMouseLeave = {(!isProjectActive && currentUser?.role !== 'admin') ? hideHoverTooltip : undefined}
										>
											<button
											onClick   = {onGeneratePrepayment}
											className = {`button button-text${(!isProjectActive && currentUser?.role !== 'admin') ? ' inactive' : ''}`}
											disabled  = {!isProjectActive && currentUser?.role !== 'admin'}
											>
												{t('project.askPrepayment')} <InlineSvgIcon width={24} className="svg-icon" src="/svg/icon/info.svg" aria-label={t('common.info')} />
											</button>
										</span>
										)}
									</div>
									<div className="list nopaginate">
										{prepayments.length > 0 ? (
										<DataTablePaginated
										data    = {prepayments}
										columns = {prepaymentInvoiceColumns}
										rowClasses = {(row) => !['paid', 'credited', 'unpaid'].includes(row.status) ? 'invoice-unpaid' : ''}
										/>
										) : (
											<>
											<label>{t('project.noPrepayments')}</label>
											<p>{t('project.noPrepaymentsHint')}</p>
											</>
										)}
									</div>
								</div>
								
							{/* Invoices section - visible even when project is not started */}
							<div className="invoices">
								<div className="title">
									<h2>{t('project.invoices')}</h2>
									{!isSupport && (
									<span
									style={{ display: 'inline-block', position: 'relative' }}
									onMouseEnter = {(!isProjectActive || !canGenerateInvoice) ? (e) => {
										let tooltipText = t('project.addFinishedTasksForInvoice');
										if (!isProjectActive) {
											tooltipText = t('project.startToGenerateInvoices');
										/* REPLY SOLUTION: to be removed from code in future.
										} else if (hasReplyTasks) {
											tooltipText = t('project.replyTasksBeforeInvoice');
										*/
										} else if (hasWaitingTasks) {
											tooltipText = t('project.finishWaitingTasksForInvoice');
										} else if (!hasUnbilledTasks) {
											tooltipText = t('project.allTasksBilled');
										}
										showHoverTooltip(e, tooltipText);
									} : undefined}
									onMouseLeave = {(!isProjectActive || !canGenerateInvoice) ? hideHoverTooltip : undefined}
									>
										<button
										onClick   = {(!isProjectActive || !canGenerateInvoice) ? (e) => { e.preventDefault(); e.stopPropagation(); } : onGenerateInvoice}
										className = {`button button-text ${(!isProjectActive || !canGenerateInvoice) ? 'inactive' : ''}`}
										disabled  = {!isProjectActive || !canGenerateInvoice}
										>
											{t('project.generateInvoice')} <InlineSvgIcon width={24} className="svg-icon" src="/svg/icon/info.svg" aria-label={t('common.info')} />
										</button>
									</span>
									)}
								</div>
								<div className="list nopaginate">
									{invoices.length > 0 ? (
										<DataTablePaginated
										data    = {invoices}
										columns = {invoiceColumns}
										rowClasses = {(row) => !['paid', 'credited', 'unpaid'].includes(row.status) ? 'invoice-unpaid' : ''}
										/>
									) : (
										<>
										<label>{t('project.noInvoices')}</label>
										<p>{t('project.noInvoicesHint')}</p>
										</>
									)}
								</div>
							</div>
							{creditInvoices.length > 0 && (
								<div className="credit-invoices">
									<div className="title">
										<h2>{t('project.creditInvoices')}</h2>
									</div>
									<div className="list nopaginate">
										<DataTablePaginated
										data    = {creditInvoices}
										columns = {creditInvoiceColumns}
										rowClasses = {(row) => !['paid', 'unpaid'].includes(row.status) ? 'invoice-unpaid' : ''}
										/>
									</div>
								</div>
							)}
								
								{/* Comment Section - at the end of page */}
								{isProjectActive && (
									<div className="comment-section" style={{ marginTop: '40px', paddingTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
										{project.comment && (
											<div className="comment-display" style={{ marginBottom: '15px', width: '100%' }}>
												<label style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '8px', display: 'block' }}>
													{t('project.comment')}
												</label>
												<p style={{ fontSize: '14px', lineHeight: '1.6' }}>
													{commentExpanded
														? project.comment
														: project.comment.length > maxChars
														? project.comment.substring(0, maxChars) + '...'
														: project.comment}
												</p>
												{project.comment.length > maxChars && (
													<button
														onClick={() => setCommentExpanded(!commentExpanded)}
														className="button button-text">
														{commentExpanded ? t('common.showLess') : t('common.showMore')}
													</button>
												)}
											</div>
										)}
										<button
											className={`button ${project.comment ? 'button-text' : ''}`}
											onClick={() => {
												setShowCommentModal(true);
												setCommentText(project.comment || '');
											}}
										>
											{project.comment ? t('project.editComment') : t('project.addComment')}
										</button>
									</div>
								)}
								</div>
							</div>
						</div>
					</div>
			
				</div>
			</div>
			
			{/* Tooltip for finishing task without date */}
			
			{finishTooltip.visible && (
				<div
				style={{
					position: 'fixed',
					left: finishTooltip.x,
					top: finishTooltip.y,
					background: 'rgba(255,0,0,0.8)',
					color: '#fff',
					padding: '5px 10px',
					borderRadius: '3px',
					zIndex: 1000,
					pointerEvents: 'none'
				}}
				>
				{finishTooltip.message}
				</div>
			)}
			
			{/* Tooltip for disabled button clicks */}
			
			{clickTooltip.visible && (
				<div
				style={{
					position: 'fixed',
					left: clickTooltip.x,
					top: clickTooltip.y,
					background: 'rgba(0,123,255,0.9)',
					color: '#fff',
					padding: '8px 12px',
					borderRadius: '4px',
					zIndex: 1000,
					pointerEvents: 'none',
					fontSize: '14px',
					boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
				}}
				>
				{clickTooltip.message}
				</div>
			)}
			
			{/* Tooltip for hover (follows mouse cursor) */}
			
			{hoverTooltip.visible && (() => {
				const tooltipMaxWidth = 200;
				const offset = 10;
				const screenWidth = window.innerWidth;
				const tooltipWidth = tooltipMaxWidth;
				
				// Check if tooltip would overflow on the right side
				const wouldOverflowRight = hoverTooltip.x + offset + tooltipWidth > screenWidth;
				
				// Position tooltip to the right of cursor by default, or to the left if it would overflow
				const leftPosition = wouldOverflowRight 
					? hoverTooltip.x - tooltipWidth - offset 
					: hoverTooltip.x + offset;
				
				return (
					<div
					style={{
						position: 'fixed',
						left: `${leftPosition}px`,
						top: `${hoverTooltip.y + offset}px`,
						background: '#f8f9fa',
						color: '#032097',
						padding: '8px 12px',
						borderRadius: '4px',
						border: '1px solid #e0e0e0',
						zIndex: 10000,
						pointerEvents: 'none',
						fontSize: '14px',
						maxWidth: `${tooltipMaxWidth}px`,
						wordWrap: 'break-word',
						boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
						transition: 'none'
					}}
					>
					{hoverTooltip.message}
					</div>
				);
			})()}
			
			{/* Task Modal */}
			
			{showTaskModal && taskModalData && (() => {
				const linkedInvoiceForModal = isInvoiceLineEdit && taskModalData.related_invoice
					? invoices.find((inv) => inv.invoiceNumber === taskModalData.related_invoice)
					: null;
				const editWindowEnd = linkedInvoiceForModal ? getInvoiceEditWindowEnd(linkedInvoiceForModal) : null;
				const durationDisplay = durationOptions.find((o) => String(o.value) === String(taskModalData.task_duration))?.display
					|| taskModalData.task_duration
					|| '-';
				return (
				<div className="modal task" onClick={()=>setShowTaskModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className="modalclose"
							onClick={() => {
								setShowTaskModal(false);
								setTaskModalData(null);
								setIsInvoiceLineEdit(false);
								setTaskModalErrors({});
							}}
							>
								&times;
							</span>
							<h2>{
								isInvoiceLineEdit
									? t('project.task.editInvoiceLineTitle')
									: (isEditingTask ? t('project.editTask') : t('project.addTaskModal'))
							}</h2>
						</div>
						<div className="modal-form d-flex flex-row flex-wrap">
							{isInvoiceLineEdit && (
								<div className="form-row col-full" style={{ marginBottom: '12px', padding: '10px', background: '#f5f7fa', borderRadius: '4px' }}>
									{t('project.task.editInvoiceLineBanner', {
										invoice: taskModalData.related_invoice,
										deadline: editWindowEnd ? formatDateDMYTime(editWindowEnd) : '',
									})}
								</div>
							)}
							<div className="form-row col-full row-name">
								<textarea
								placeholder = {t('project.taskDescription')}
								className   = {"" + (taskModalErrors.task_name ? " invalid" : "")}
								value       = {taskModalData.task_name}
								onChange    = {(e) => setTaskModalData(prev => ({ ...prev, task_name: e.target.value }))}
								rows        = {5}
								/>
								{taskModalErrors.task_name && <div className="error">{taskModalErrors.task_name}</div>}
							</div>
							{isInvoiceLineEdit ? (
								<>
									<div className="form-row col-half row-duration">
										<label>{t('project.duration')}</label>
										<div>{durationDisplay}</div>
									</div>
									<div className="form-row col-half row-date">
										<label>{t('project.date')}</label>
										<input
										type      = "date"
										className = {"" + (taskModalErrors.task_date ? " invalid" : "")}
										value     = {taskModalData.task_date}
										onChange  = {(e) => setTaskModalData(prev => ({ ...prev, task_date: e.target.value }))}
										/>
										{taskModalErrors.task_date && <div style={{ color: 'red' }}>{taskModalErrors.task_date}</div>}
									</div>
									<div className="form-row col-half">
										<label>{t('project.table.invoice')}</label>
										<div>{taskModalData.related_invoice}</div>
									</div>
								</>
							) : (
								<>
							<div className="form-row col-half row-duration">
								<label>{t('project.duration')}</label>
								<select
								className = {"" + (taskModalErrors.task_duration ? " invalid" : "")}
								value     = {taskModalData.task_duration}
								onChange  = {(e) => setTaskModalData(prev => ({ ...prev, task_duration: e.target.value }))}
								>
									<option value = "">{t('project.selectDuration')}</option>
									<option value = "0">00:00</option>
									{durationOptions.map((option, index) => (
										<option key={index} value={option.value}>
											{option.display}
										</option>
									))}
								</select>
								{taskModalErrors.task_duration && <div className="error">{taskModalErrors.task_duration}</div>}
							</div>
							<div className="form-row col-half row-status">
								<label>{t('project.status')}</label>
								<select
								className = ""
								value     = {taskModalData.status}
								onChange  = {(e) => setTaskModalData(prev => ({ ...prev, status: e.target.value }))}
								>
									<option value = "done">{t('project.statusDone')}</option>
									<option value = "waiting">{t('project.statusWaiting')}</option>
								</select>
							</div>
							{taskModalData.status === 'done' ? (
								<div className="form-row col-half row-date">
									<label>{t('project.date')}</label>
									<input
									type      = "date"
									className = {"" + (taskModalErrors.task_date ? " invalid" : "")}
									value     = {taskModalData.task_date}
									onChange  = {(e) => setTaskModalData(prev => ({ ...prev, task_date: e.target.value }))}
									/>
									{taskModalErrors.task_date && <div style={{ color: 'red' }}>{taskModalErrors.task_date}</div>}
								</div>
							) : (
								<div className="form-row col-half row-deadline">
									<label>{t('project.deadline')}</label>
									<input
									type      = "date"
									value     = {taskModalData.deadline}
									onChange  = {(e) => setTaskModalData(prev => ({ ...prev, deadline: e.target.value }))}
									/>
								</div>
							)}
							{currentUser?.role === 'admin' && (
							<div className="form-row col-half row-billed">
								<label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
									<input
									type     = "checkbox"
									checked  = {taskModalData.task_billed || false}
									onChange = {(e) => setTaskModalData(prev => ({ ...prev, task_billed: e.target.checked }))}
									style    = {{ cursor: 'pointer' }}
									/>
									<span>{t('project.billed')}</span>
								</label>
							</div>
							)}
								</>
							)}
							<div className="form-row col-full row-actions">
								<button
								className = "button button-large"
								onClick   = {() => {
									setShowTaskModal(false);
									setTaskModalData(null);
									setIsInvoiceLineEdit(false);
									setTaskModalErrors({});
								}}
								>
									{t('common.cancel')}
								</button>
								<button
								className = "button button-large button-blue"
								onClick   = {handleSaveTaskModal}
								>
									{isEditingTask ? t('common.update') : t('common.create')}
								</button>
							</div>
						</div>
					</div>
				</div>
				);
			})()}
			
			{/* Comment Modal */}
			
			{showCommentModal && (
				<div className="modal comment" onClick={()=>setShowCommentModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className = "modalclose"
							onClick   = {() => setShowCommentModal(false)}
							>
								&times;
							</span>
							<h2>{project.comment ? t('project.editComment') : t('project.addComment')}</h2>
						</div>
						<div className="modal-form d-flex flex-row flex-wrap">
							<div className="form-row col-full row-text">
								<p>
									{t('project.commentPrivateNote')}
								</p>
							</div>
							<div className="form-row col-full row-comment">
								<textarea
								value    = {commentText}
								onChange = {(e) => setCommentText(e.target.value)}
								/>
							</div>
							<div className="form-row col-full row-actions">
								<button
								className = "button button-large"
								onClick   = {() => setShowCommentModal(false)}
								>
									{t('common.cancel')}
								</button>
								<button
								className = "button button-large button-blue"
								onClick   = {async () => {
									try {
										await axios.patch(`/projects/${project._id}`, { comment: commentText });
										refreshProject();
										setShowCommentModal(false);
										setCommentText('');
									} catch (error) {
										logger.error('Error saving comment:', error);
									}
								}}
								>
									{project.comment ? t('common.update') : t('common.create')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
			
			{/* REPLY SOLUTION: to be removed from code in future. Initial Response Modal and Send Confirmation Modal removed. */}
			
			{/* Pre-payment Modal */}
			
			{showPrepaymentModal && (
				<div className="modal prepayment" onClick={()=>setShowPrepaymentModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className ="modalclose"
							onClick   = {() => {
								setShowPrepaymentModal(false);
								setPrepaymentFeedback('');
								setShowPrepaymentConfirmation(false);
							}}
							>
								&times;
							</span>
							<h2>{t('project.requestPrepayment')}</h2>
						</div>
						<div className="modal-form d-flex flex-row flex-wrap">
							{!showPrepaymentConfirmation && (
								<>
									<div className="form-row col-thin row-duration">
										<label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('project.serviceDuration')} <span style={{color: 'red'}}>*</span></label>
										<div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
									<div style={{ flex: 1 }}>
										<label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>{t('common.hours')}</label>
										<select
											value={prepaymentHours}
											onChange={(e) => setPrepaymentHours(parseInt(e.target.value))}
											style={{ width: '100%' }}
										>
													{[...Array(31)].map((_, i) => {
														return (
															<option key={i} value={i}>
																{i} {i === 1 ? t('common.hour') : t('common.hours')}
															</option>
														);
													})}
												</select>
											</div>
									<div style={{ flex: 1 }}>
										<label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>{t('common.min')}</label>
										<select
											value={prepaymentMinutes}
											onChange={(e) => setPrepaymentMinutes(parseInt(e.target.value))}
											style={{ width: '100%' }}
										>
													{[0, 10, 20, 30, 40, 50].map((mins) => {
														return (
															<option key={mins} value={mins}>
																{mins.toString().padStart(2, '0')} min
															</option>
														);
													})}
												</select>
											</div>
										</div>
									</div>
									<div className="form-row col-thin row-total" style={{ marginTop: '6px', marginBottom: '20px', padding: '12px 0' }}>
										<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
											<span style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>{t('project.totalAmount')}</span>
											<span style={{ fontSize: '16px', fontWeight: '600', color: '#032097' }}>
												{((((prepaymentHours * 60) + prepaymentMinutes) / 60) * getEffectiveHourlyRate()).toFixed(2)} €
											</span>
										</div>
										<div style={{ fontSize: '11px', color: '#666', borderTop: '1px solid #e9ecef', paddingTop: '6px' }}>
											{t('project.hourlyRateLabel')} {getEffectiveHourlyRate().toFixed(2)} €
										</div>
									</div>
									<div className="form-row col-thin row-comment">
										<label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('project.paymentDescription')} <span style={{color: 'red'}}>*</span></label>
										<input
										type     = "text"
										value    = {prepaymentComment}
										onChange = {(e) => setPrepaymentComment(e.target.value)}
										placeholder={t('project.paymentDescriptionPlaceholder')}
										required
										/>
										{prepaymentComment && prepaymentComment.trim().length < 5 && (
											<span style={{color: '#e74c3c', fontSize: '12px', marginTop: '5px', display: 'block'}}>
												{t('project.descriptionMinLength')}
											</span>
										)}
									</div>
									<div className="form-row col-thin row-duedate">
										<label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('project.paymentDueIn')}</label>
										<select
										value    = {prepaymentDueDays}
										onChange = {(e) => setPrepaymentDueDays(parseInt(e.target.value))}
										>
											{[...Array(14)].map((_, i) => {
												const days = i + 1;
												return (
													<option key={days} value={days}>
														{days} {days === 1 ? t('common.day') : t('common.days')}
													</option>
												);
											})}
										</select>
									</div>
								</>
							)}
							{prepaymentFeedback ? (
							<>
								<div className="form-row col-thin row-desc">
									<p>{prepaymentFeedback}</p>
								</div>
								<div className="form-row col-full row-actions">
									<button
									className = "button button-large button-blue"
									onClick   = {() => {
										setShowPrepaymentModal(false);
										setPrepaymentFeedback('');
										setPrepaymentHours(0);
										setPrepaymentMinutes(0);
										setPrepaymentComment('');
										setShowPrepaymentConfirmation(false);
									}}
									>
										{t('common.close')}
									</button>
								</div>
							</>
							) : (
							<>
								{showPrepaymentConfirmation ? (
								<>
									<div className="form-row col-full row-label">
										<p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
											{t('project.prepaymentConfirmText')}
										</p>
									</div>
									<div className="form-row col-full row-actions no-margin">
										<button
										className = "button button-large"
										onClick   = {() => setShowPrepaymentConfirmation(false)}
										disabled  = {isPrepaymentProcessing}
										>
											{t('common.cancel')}
										</button>
										<button
										className = "button button-large button-blue"
										onClick   = {confirmPrepayment}
										disabled  = {isPrepaymentProcessing}
										>
											{isPrepaymentProcessing ? t('common.processing') : t('project.confirmAndSend')}
										</button>
									</div>
									{/* PDF Template Preview */}
									{(() => {
											const totalMinutes = (prepaymentHours * 60) + prepaymentMinutes;
											const hourlyRate = getEffectiveHourlyRate();
											const amountTotal = ((totalMinutes / 60) * hourlyRate);
											const vatPercentage = getVatPercentage();
											const amountNet = amountTotal / (1 + vatPercentage);
											const vatAmount = amountTotal - amountNet;
											const hoursMinutes = `${prepaymentHours}:${prepaymentMinutes.toString().padStart(2, '0')}`;
											const currentDate = new Date().toLocaleDateString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' });
											const dueDate = new Date();
											dueDate.setDate(dueDate.getDate() + prepaymentDueDays);
											const dueDateFormatted = dueDate.toLocaleDateString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' });
											
										return (
											<div style={{ 
												backgroundColor: '#ffffff', 
												padding: '50px 60px', 
												borderRadius: '4px', 
												border: '1px solid #e0e0e0',
												boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
												fontFamily: "'Montserrat', Verdana, sans-serif",
												maxWidth: '595px',
												margin: '20px auto 20px auto',
												fontSize: '8pt',
												color: '#000000',
												lineHeight: '1.4'
											}}>
													{/* Header */}
													<div style={{ 
														display: 'flex', 
														justifyContent: 'space-between', 
														alignItems: 'flex-start',
														marginBottom: '30px'
													}}>
														<div style={{ width: '126px' }}>
															<img src="https://hugo.legal/wp-content/uploads/2025/01/hugologo2.png" alt="HUGO.legal" style={{ width: '126px' }} />
														</div>
														<div style={{ textAlign: 'right', flex: 1 }}>
															<div style={{ fontSize: '12pt', fontWeight: '700', color: '#000', marginBottom: '4px' }}>
																Ettemaksuarve: [Draft]
															</div>
															<div style={{ fontSize: '8pt', color: '#666', lineHeight: '1.6' }}>
																<div>Kuupäev: {currentDate}</div>
																<div>Tähtaeg: {dueDateFormatted}</div>
															</div>
														</div>
													</div>
													
													{/* Parties */}
													<div style={{ margin: '25px 0' }}>
														<div style={{ marginBottom: '15px', textAlign: 'left' }}>
															<div style={{ fontSize: '8pt', color: '#666', marginBottom: '3px' }}>Ostja:</div>
															{isBusinessClient() ? (
																<>
																	<div style={{ fontSize: '8pt', fontWeight: '700', color: '#000', marginBottom: '2px' }}>
																		{client?.business_name || project?.business_name || getRepresentativeName()}
																	</div>
																	{client?.business_address || project?.business_address ? (
																		<div style={{ fontSize: '8pt', color: '#666', lineHeight: '1.4' }}>
																			{client?.business_address || project?.business_address}
																		</div>
																	) : null}
																</>
															) : (
																<div style={{ fontSize: '8pt', fontWeight: '700', color: '#000' }}>
																	{getRepresentativeName()}
																</div>
															)}
														</div>
														
														<div style={{ marginBottom: '15px', textAlign: 'left' }}>
															<div style={{ fontSize: '8pt', color: '#666', marginBottom: '3px' }}>Teenuseosutaja:</div>
															<div style={{ fontSize: '8pt', fontWeight: '700', color: '#000', marginBottom: '2px' }}>
																{owner?.partner_company_name || 'OÜ HUGO'}
															</div>
															<div style={{ fontSize: '8pt', color: '#666', lineHeight: '1.4' }}>
																{owner?.partner_company_address || ''}
																{owner?.partner_company_reg_id ? <><br />Registrikood: {owner.partner_company_reg_id}</> : null}
																{owner?.partner_vat && owner?.partner_company_vat_id ? <><br />KMKR nr: {owner.partner_company_vat_id}</> : null}
															</div>
														</div>
													</div>
													
													{/* Invoice Table */}
													<table style={{ 
														width: '100%', 
														borderCollapse: 'collapse', 
														margin: '25px 0'
													}}>
														<thead>
															<tr>
																<th style={{ 
																	backgroundColor: '#f5f5f5', 
																	border: '1pt solid #ddd', 
																	padding: '8pt', 
																	textAlign: 'left', 
																	fontWeight: '700', 
																	fontSize: '8pt', 
																	color: '#000' 
																}}>Kirjeldus</th>
																<th style={{ 
																	backgroundColor: '#f5f5f5', 
																	border: '1pt solid #ddd', 
																	padding: '8pt', 
																	textAlign: 'center', 
																	fontWeight: '700', 
																	fontSize: '8pt', 
																	color: '#000',
																	width: '100pt'
																}}>Kogus</th>
																<th style={{ 
																	backgroundColor: '#f5f5f5', 
																	border: '1pt solid #ddd', 
																	padding: '8pt', 
																	textAlign: 'center', 
																	fontWeight: '700', 
																	fontSize: '8pt', 
																	color: '#000',
																	width: '100pt'
																}}>Hind</th>
																<th style={{ 
																	backgroundColor: '#f5f5f5', 
																	border: '1pt solid #ddd', 
																	padding: '8pt', 
																	textAlign: 'center', 
																	fontWeight: '700', 
																	fontSize: '8pt', 
																	color: '#000',
																	width: '120pt'
																}}>Summa</th>
															</tr>
														</thead>
														<tbody>
															<tr>
																<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt' }}>
																	{prepaymentComment || 'Ettemaks õigusteenuse eest'}
																</td>
																<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', textAlign: 'right' }}>
																	{hoursMinutes}
																</td>
																<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', textAlign: 'right' }}>
																	{hourlyRate.toFixed(2)} €
																</td>
																<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', textAlign: 'right' }}>
																	{amountNet.toFixed(2)} €
																</td>
															</tr>
														</tbody>
													</table>
													
													{/* Totals */}
													<div style={{ margin: '25px 0 40px auto', width: '400pt', textAlign: 'right', maxWidth: '100%', overflow: 'hidden' }}>
														<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', justifyContent: 'flex-end' }}>
															<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>Summa käibemaksuta (EUR):</span>
															<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{amountNet.toFixed(2)} €</span>
														</div>
														{vatPercentage > 0 && (
															<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', justifyContent: 'flex-end' }}>
																<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>Käibemaks {Math.round(vatPercentage * 100)}%:</span>
																<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{vatAmount.toFixed(2)} €</span>
															</div>
														)}
														<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', fontWeight: '700', justifyContent: 'flex-end' }}>
															<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>Summa kokku (EUR):</span>
															<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{amountTotal.toFixed(2)} €</span>
														</div>
													</div>
													
													{/* Signature */}
													<div style={{ fontSize: '8pt', lineHeight: '1.5', textAlign: 'left' }}>
														<div style={{ fontWeight: 'normal' }}>{owner ? `${owner.firstName} ${owner.lastName}` : ''}</div>
														<div>{owner?.partner_title || ''}</div>
													</div>
													
													{/* Payment Info */}
													<div style={{ marginTop: '40px', textAlign: 'right', fontSize: '8pt', lineHeight: '1.6', color: '#000' }}>
														<div style={{ fontWeight: '700', marginBottom: '5px' }}>Makseinfo:</div>
														<div>Makse saaja: OÜ HUGO</div>
														<div>Panga nimi: AS LHV Pank</div>
														<div>Pangakonto: EE587700771001523475</div>
													</div>
												</div>
											);
										})()}
									</>
								) : (
									<div className="form-row col-full row-actions">
										<button
										className = "button button-large"
										onClick   = {() => setShowPrepaymentModal(false)}
										>
											{t('common.cancel')}
										</button>
										<button
										className = {`button button-large button-blue ${((prepaymentHours === 0 && prepaymentMinutes === 0) || !prepaymentComment || prepaymentComment.trim().length < 5) ? 'inactive' : ''}`}
										onClick   = {() => setShowPrepaymentConfirmation(true)}
										disabled  = {(prepaymentHours === 0 && prepaymentMinutes === 0) || !prepaymentComment || prepaymentComment.trim().length < 5}
										>
											{t('common.continue')}
										</button>
									</div>
								)}
							</>
							)}
						</div>
					</div>
				</div>
			)}
			
			{/* Invoice Modal */}
			
			{showGenerateInvoiceModal && (
				<div className="modal invoice" onClick={()=>setShowGenerateInvoiceModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className ="modalclose"
							onClick   = {() => {
								setShowGenerateInvoiceModal(false);
								setInvoiceFeedback('');
								setShowInvoiceConfirmation(false);
							}}
							>
								&times;
							</span>
							<h2>{t('project.generateInvoiceTitle')}</h2>
						</div>
						<div className="modal-form d-flex flex-row flex-wrap">
							{invoiceFeedback ? (
							<>
								<div className="form-row col-thin row-text">
									<p>{invoiceFeedback}</p>
								</div>
								<div className="form-row col-full row-actions">
									<button
									className = "button button-large button-blue"
									onClick   = {() => {
										setShowGenerateInvoiceModal(false);
										setInvoiceFeedback('');
									}}
									>
										{t('common.close')}
									</button>
								</div>
							</>
							) : (
							<>
							{showInvoiceConfirmation ? (
							<>
								<div className="form-row col-full row-label row-label-nopadding">
									<p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
										{t('project.invoiceConfirmText')}
									</p>
								</div>
								<div className="form-row col-full row-actions no-margin">
									<button
									className = "button button-large"
									onClick   = {() => setShowInvoiceConfirmation(false)}
									disabled  = {isInvoiceProcessing}
									>
										{t('common.cancel')}
									</button>
									<button
									className = "button button-large button-blue"
									onClick   = {() => handleConfirmGenerateInvoice(true)}
									disabled  = {isInvoiceProcessing}
									>
										{isInvoiceProcessing ? t('common.processing') : t('project.confirmAndSend')}
									</button>
								</div>
								{/* PDF Template Preview */}
								{(() => {
										const amountTotal = parseFloat(finalInvoiceAmount);
										const vatPercentage = getVatPercentage();
										const amountNet = amountTotal / (1 + vatPercentage);
										const vatAmount = amountTotal - amountNet;
										const prepaymentApplied = parseFloat(prepaymentAmountBilled) || 0;
										const amountDue = Math.max(0, amountTotal - prepaymentApplied);
										
										const currentDate = new Date().toLocaleDateString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' });
										const dueDate = new Date();
										dueDate.setDate(dueDate.getDate() + invoiceDueDays);
										const dueDateFormatted = dueDate.toLocaleDateString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' });
										
										const unbilledTasks = project?.related_tasks?.filter(t => !t.task_billed) || [];
										
										return (
											<>
											<div style={{ 
												backgroundColor: '#ffffff', 
												padding: '50px 60px', 
												borderRadius: '4px', 
												border: '1px solid #e0e0e0',
												boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
												fontFamily: "'Montserrat', Verdana, sans-serif",
												width: '595px',
												maxWidth: '595px',
												margin: '20px auto 20px auto',
												fontSize: '8pt',
												color: '#000000',
												lineHeight: '1.4'
											}}>
												{/* Header */}
												<div style={{ 
													display: 'flex', 
													justifyContent: 'space-between', 
													alignItems: 'flex-start',
													marginBottom: '30px'
												}}>
													<div style={{ width: '126px' }}>
														<img src="https://hugo.legal/wp-content/uploads/2025/01/hugologo2.png" alt="HUGO.legal" style={{ width: '126px' }} />
													</div>
													<div style={{ textAlign: 'right', flex: 1 }}>
														<div style={{ fontSize: '12pt', fontWeight: '700', color: '#000', marginBottom: '4px' }}>
															Arve: [Draft]
														</div>
														<div style={{ fontSize: '8pt', color: '#666', lineHeight: '1.6' }}>
															<div>Kuupäev: {currentDate}</div>
															{amountDue > 0 && <div>Tähtaeg: {dueDateFormatted}</div>}
															{amountDue === 0 && <div>Makstud</div>}
														</div>
													</div>
												</div>
												
												{/* Parties */}
												<div style={{ margin: '25px 0' }}>
													<div style={{ marginBottom: '15px', textAlign: 'left' }}>
														<div style={{ fontSize: '8pt', color: '#666', marginBottom: '3px' }}>Ostja:</div>
														{isBusinessClient() ? (
															<>
																<div style={{ fontSize: '8pt', fontWeight: '700', color: '#000', marginBottom: '2px' }}>
																	{client?.business_name || project?.business_name || getRepresentativeName()}
																</div>
																{client?.business_address || project?.business_address ? (
																	<div style={{ fontSize: '8pt', color: '#666', lineHeight: '1.4' }}>
																		{client?.business_address || project?.business_address}
																	</div>
																) : null}
															</>
														) : (
															<div style={{ fontSize: '8pt', fontWeight: '700', color: '#000' }}>
																{getRepresentativeName()}
															</div>
														)}
													</div>
													
													<div style={{ marginBottom: '15px', textAlign: 'left' }}>
														<div style={{ fontSize: '8pt', color: '#666', marginBottom: '3px' }}>Teenuseosutaja:</div>
														<div style={{ fontSize: '8pt', fontWeight: '700', color: '#000', marginBottom: '2px' }}>
															{owner?.partner_company_name || 'OÜ HUGO'}
														</div>
														<div style={{ fontSize: '8pt', color: '#666', lineHeight: '1.4' }}>
															{owner?.partner_company_address || ''}
															{owner?.partner_company_reg_id ? <><br />Registrikood: {owner.partner_company_reg_id}</> : null}
															{owner?.partner_vat && owner?.partner_company_vat_id ? <><br />KMKR nr: {owner.partner_company_vat_id}</> : null}
														</div>
													</div>
												</div>
												
												{/* Invoice Table */}
												<table style={{ 
													width: '100%', 
													borderCollapse: 'collapse', 
													margin: '25px 0'
												}}>
													<thead>
														<tr>
															<th style={{ 
																backgroundColor: '#f5f5f5', 
																border: '1pt solid #ddd', 
																padding: '8pt', 
																textAlign: 'left', 
																fontWeight: '700', 
																fontSize: '8pt', 
																color: '#000' 
															}}>Kirjeldus</th>
															<th style={{ 
																backgroundColor: '#f5f5f5', 
																border: '1pt solid #ddd', 
																padding: '8pt', 
																textAlign: 'center', 
																fontWeight: '700', 
																fontSize: '8pt', 
																color: '#000',
																width: '100pt'
															}}>Summa (EUR)</th>
															<th style={{ 
																backgroundColor: '#f5f5f5', 
																border: '1pt solid #ddd', 
																padding: '8pt', 
																textAlign: 'center', 
																fontWeight: '700', 
																fontSize: '8pt', 
																color: '#000',
																width: '100pt'
															}}>KM {Math.round(vatPercentage * 100)}%</th>
															<th style={{ 
																backgroundColor: '#f5f5f5', 
																border: '1pt solid #ddd', 
																padding: '8pt', 
																textAlign: 'center', 
																fontWeight: '700', 
																fontSize: '8pt', 
																color: '#000',
																width: '120pt'
															}}>Summa KM-ga (EUR)</th>
														</tr>
													</thead>
													<tbody>
														<tr>
															<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt' }}>
																Õigusteenuse tasu
															</td>
															<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', textAlign: 'right' }}>
																{amountNet.toFixed(2)} €
															</td>
															<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', textAlign: 'right' }}>
																{vatAmount.toFixed(2)} €
															</td>
															<td style={{ borderLeft: '1pt solid #ddd', borderRight: '1pt solid #ddd', borderTop: '1pt solid #ddd', borderBottom: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', textAlign: 'right' }}>
																{amountTotal.toFixed(2)} €
															</td>
														</tr>
													</tbody>
												</table>
												
												{/* Totals */}
												<div style={{ margin: '25px 0 40px auto', width: '400pt', textAlign: 'right', maxWidth: '100%', overflow: 'hidden' }}>
													<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', justifyContent: 'flex-end' }}>
														<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>Summa käibemaksuta (EUR):</span>
														<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{amountNet.toFixed(2)} €</span>
													</div>
													{vatPercentage > 0 && (
														<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', justifyContent: 'flex-end' }}>
															<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>KM {Math.round(vatPercentage * 100)}%:</span>
															<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{vatAmount.toFixed(2)} €</span>
														</div>
													)}
													<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', fontWeight: '700', justifyContent: 'flex-end' }}>
														<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>Summa kokku (EUR):</span>
														<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{amountTotal.toFixed(2)} €</span>
													</div>
													{prepaymentApplied > 0 && (
														<>
															<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', justifyContent: 'flex-end' }}>
																<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>Ettemaks (EUR):</span>
																<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{prepaymentApplied.toFixed(2)} €</span>
															</div>
															<div style={{ display: 'flex', padding: '4pt 0', fontSize: '8pt', fontWeight: '700', justifyContent: 'flex-end' }}>
																<span style={{ textAlign: 'right', flex: 1, whiteSpace: 'nowrap', minWidth: 0, marginRight: '20pt' }}>Tasumisele kuulub (EUR):</span>
																<span style={{ textAlign: 'right', width: '100pt', flexShrink: 0 }}>{amountDue.toFixed(2)} €</span>
															</div>
														</>
													)}
												</div>
												
												{/* Thank you */}
												<div style={{ fontSize: '8pt', marginBottom: '20px', textAlign: 'left' }}>
													Täname meeldiva koostöö eest!
												</div>
												
												{/* Signature */}
												<div style={{ fontSize: '8pt', lineHeight: '1.5', textAlign: 'left', marginBottom: '30px' }}>
													<div style={{ fontWeight: 'normal' }}>{owner ? `${owner.firstName} ${owner.lastName}` : ''}</div>
													<div>{owner?.partner_title || ''}</div>
												</div>
												
												{/* Payment Info */}
												<div style={{ textAlign: 'right', fontSize: '8pt', lineHeight: '1.6', color: '#000' }}>
													<div style={{ fontWeight: '700', marginBottom: '5px' }}>Makseinfo:</div>
													<div>Makse saaja: OÜ HUGO</div>
													<div>Panga nimi: AS LHV Pank</div>
													<div>Pangakonto: EE587700771001523475</div>
												</div>
											</div>
											
											{/* Second Page: Task Details */}
											{unbilledTasks && unbilledTasks.length > 0 && (
												<div style={{ 
													backgroundColor: '#ffffff', 
													padding: '50px 60px', 
													borderRadius: '4px', 
													border: '1px solid #e0e0e0',
													boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
													marginTop: '20px',
													marginBottom: '20px',
													fontFamily: "'Montserrat', Verdana, sans-serif",
													width: '595px',
													maxWidth: '595px',
													margin: '20px auto',
													fontSize: '8pt',
													color: '#000000',
													lineHeight: '1.4'
												}}>
													{/* Header */}
													<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px' }}>
														<div style={{ width: '126px' }}>
															<img src="https://hugo.legal/wp-content/uploads/2025/01/hugologo2.png" alt="HUGO.legal" style={{ width: '126px' }} />
														</div>
														<div style={{ textAlign: 'right', flex: 1 }}>
															<div style={{ fontSize: '12pt', fontWeight: '700', color: '#000', marginBottom: '4px' }}>
																Arve: [Draft]
															</div>
															<div style={{ fontSize: '8pt', color: '#666', lineHeight: '1.6' }}>
																<div>Kuupäev: {currentDate}</div>
															</div>
														</div>
													</div>
													
													{/* Tasks Section */}
													<div>
														<h2 style={{ fontSize: '14pt', fontWeight: '700', marginBottom: '15pt', textAlign: 'left' }}>Tööde kirjeldus</h2>
														
														<div style={{ marginBottom: '20pt', fontSize: '8pt', lineHeight: '1.5', textAlign: 'left' }}>
															<div><strong>Arve nr.</strong> [Draft]</div>
															<div><strong>Kaasus:</strong> {project?.project_name || project?.project_no || ''}</div>
														</div>
														
														<div style={{ fontSize: '10pt', fontWeight: '700', margin: '20pt 0 10pt 0', textAlign: 'left' }}>Tehtud tööd</div>
														
														<table style={{ 
															width: '100%', 
															borderCollapse: 'collapse', 
															margin: '15pt 0'
														}}>
															<thead>
																<tr>
																	<th style={{ backgroundColor: '#f5f5f5', border: '1pt solid #ddd', padding: '8pt', textAlign: 'left', fontWeight: '700', fontSize: '8pt', width: '100pt' }}>Kuupäev</th>
																	<th style={{ backgroundColor: '#f5f5f5', border: '1pt solid #ddd', padding: '8pt', textAlign: 'left', fontWeight: '700', fontSize: '8pt' }}>Sisu</th>
																	<th style={{ backgroundColor: '#f5f5f5', border: '1pt solid #ddd', padding: '8pt', textAlign: 'left', fontWeight: '700', fontSize: '8pt', width: '100pt' }}>Kestus</th>
																</tr>
															</thead>
															<tbody>
																{unbilledTasks.map((task, idx) => {
																	const minutes = parseFloat(task.task_duration) || 0;
																	const hours = minutes / 60;
																	const formattedDuration = `${Math.floor(hours)}:${String(Math.round((hours % 1) * 60)).padStart(2, '0')}`;
																	const taskDate = task.task_date ? new Date(task.task_date).toLocaleDateString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
																	
																	return (
																		<tr key={idx}>
																			<td style={{ border: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', verticalAlign: 'top' }}>{taskDate}</td>
																			<td style={{ border: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', verticalAlign: 'top' }}>{task.task_name}</td>
																			<td style={{ border: '1pt solid #ddd', padding: '8pt', fontSize: '8pt', verticalAlign: 'top' }}>{formattedDuration}</td>
																		</tr>
																	);
																})}
															</tbody>
														</table>
														
														<div style={{ margin: '20pt 0 20pt 0', fontSize: '8pt', fontWeight: '700' }}>
															<div style={{ display: 'flex', justifyContent: 'space-between', padding: '4pt 0', fontSize: '8pt', fontWeight: '700' }}>
																<span>Kokku (h)</span>
																<span>{(() => {
																	const totalMinutes = unbilledTasks.reduce((sum, t) => sum + (parseFloat(t.task_duration) || 0), 0);
																	const totalHours = totalMinutes / 60;
																	return `${Math.floor(totalHours)}:${String(Math.round((totalHours % 1) * 60)).padStart(2, '0')}`;
																})()}</span>
															</div>
															<div style={{ display: 'flex', justifyContent: 'space-between', padding: '4pt 0', fontSize: '8pt', fontWeight: '700' }}>
																<span>Kokku neto (EUR)</span>
																<span>{amountNet.toFixed(2)} €</span>
															</div>
														</div>
													</div>
												</div>
											)}
											</>
										);
									})()}
							</>
							) : (
								<>
									<div className="form-row col-thin row-desc">
										<div className="list-desc">
											<dl>
												<dt>{t('project.unbilledTasks')}</dt>
												<dd>{unbilledTasksTotal} €</dd>
											</dl>
											<dl>
												<dt>{t('project.unbilledPrepayments')}</dt>
												<dd>{paidPrepaymentsTotal} €</dd>
											</dl>
											<dl>
												<dt>{t('project.billedFromPrepayment')}</dt>
												<dd>{prepaymentAmountBilled} €</dd>
											</dl>
											<dl className="total">
												<dt>{t('project.total')}</dt>
												<dd>{finalInvoiceAmount} €</dd>
											</dl>
											{canPrepaymentsCoverInvoice() && (
												<dl className="info">
													<dt>{t('project.table.status')}</dt>
													<dd><strong>{t('project.fullyCoveredByPrepayments')}</strong></dd>
												</dl>
											)}
										</div>
									</div>
									<div className="form-row col-thin row-duedate">
										<label>{t('project.dueDateDays')}</label>
										<select
										value    = {invoiceDueDays}
										onChange = {(e) => setInvoiceDueDays(parseInt(e.target.value))}
										>
											{[...Array(14)].map((_, i) => {
												const days = i + 1;
												return (
													<option key={days} value={days}>
														{days} {days === 1 ? t('common.day') : t('common.days')}
													</option>
												);
											})}
										</select>
									</div>
									<div className="form-row col-full row-actions">
										<button
										className = "button button-large"
										onClick   = {() => setShowGenerateInvoiceModal(false)}
										>
											{t('common.cancel')}
										</button>
										{/* Show Create and send button when there are unbilled tasks (regardless of prepayment coverage) */}
										{finalInvoiceAmount >= 0 && (
											<button
											className = "button button-large button-blue"
											onClick   = {() => setShowInvoiceConfirmation(true)}
											>
												{t('project.createAndSend')}
											</button>
										)}
									</div>
								</>
								)}
							</>
							)}
						</div>
					</div>
				</div>
			)}
			
			{/* Finish Project Modal */}
			
			{showFinishProjectModal && project && (
				<div className="modal finish" onClick={()=>setShowFinishProjectModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className = "modalclose"
							onClick   = {() => setShowFinishProjectModal(false)}
							>
								&times;
							</span>
							<h2>{t('project.finishProjectTitle')}</h2>
						</div>
						<div className="modal-form d-flex flex-row flex-wrap">
							<div className="form-row col-full row-label">
								<h3>{t('project.finishConfirm')}</h3>
							</div>
							<div className="form-row col-full row-actions">
								<button
								className = "button button-large"
								onClick   = {() => setShowFinishProjectModal(false)}
								>
									{t('common.cancel')}
								</button>
								<button
								className = "button button-large button-blue"
								onClick   = {() => handleFinishProject(true)}
								>
									{t('project.finishProject')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

		{/* Change Owner Modal */}
		
		{showChangeOwnerModal && (
			<div className="modal change-owner" onClick={()=>setShowChangeOwnerModal(false)}>
				<div className="modal-content" onClick={e=>e.stopPropagation()}>
				<div className="modal-header">
					<span
					className = "modalclose"
					onClick   = {() => {
						setShowChangeOwnerModal(false);
						setSelectedNewOwnerId('');
						setMatchLawyers([]);
						setMatchCriteria(null);
						/* REPLY SOLUTION: to be removed from code in future.
						setClearInitialResponse(false);
						setRestoreReplyTaskIndex(null);
						*/
						setOwnerChangeFeedback([]);
						setReassignHourlyRate('');
					}}
					>
						&times;
					</span>
					<h2>{t('project.reassignProjectTitle')}</h2>
				</div>
				<div className="modal-form d-flex flex-row flex-wrap">
					{ownerChangeFeedback.length > 0 ? (
						<>
							<div className="form-row col-thin row-desc" style={{ textAlign: 'center', padding: '20px 0', width: '100%' }}>
								{ownerChangeFeedback.map((msg, i) => (
									<p key={i} style={{ margin: '6px 0' }}>{msg}</p>
								))}
							</div>
							<div className="form-row col-full row-actions">
								<button
									className="button button-large button-blue"
									onClick={() => {
										setShowChangeOwnerModal(false);
										setSelectedNewOwnerId('');
										setMatchLawyers([]);
										setMatchCriteria(null);
										/* REPLY SOLUTION: to be removed from code in future.
										setClearInitialResponse(false);
										setRestoreReplyTaskIndex(null);
										*/
										setOwnerChangeFeedback([]);
										setReassignHourlyRate('');
									}}
								>
									{t('common.close')}
								</button>
							</div>
						</>
					) : (
						<>
							{matchCriteria && (matchCriteria.legalFields?.length > 0 || matchCriteria.legalField) && (
								<div className="form-row col-full">
									<div className="change-owner-match-summary">
										<span>{t('project.matchSummaryActive')}</span>
										<span>{t('project.matchSummaryLocale', { locale: matchCriteria.locale || 'et' })}</span>
										<span>
											{t('project.matchSummaryLegalField', {
												field: (matchCriteria.legalFields || [])
													.map((f) => f.name_et)
													.filter(Boolean)
													.join(', ') || matchCriteria.legalField?.name_et || t('project.matchSummaryNoLegalField'),
											})}
										</span>
										{matchCriteria.clientType === 'business' && (
											<span>{t('project.matchSummaryBusiness')}</span>
										)}
									</div>
								</div>
							)}
							<div className="form-row col-full row-owner-select">
								{ownerChangeLoading && matchLawyers.length === 0 ? (
									<p className="change-owner-empty">{t('common.loading')}</p>
								) : !(matchCriteria?.legalFields?.length || matchCriteria?.legalField) ? (
									<p className="change-owner-empty">{t('project.matchLawyersNoLegalField')}</p>
								) : matchLawyers.length === 0 ? (
									<p className="change-owner-empty">{t('project.matchLawyersEmpty')}</p>
								) : (
									<div className="change-owner-lawyer-grid" role="listbox" aria-label={t('project.reassignProjectTitle')}>
										{matchLawyers.map((lawyer) => {
											const isSelected = String(selectedNewOwnerId) === String(lawyer.user_no);
											const name = `${lawyer.firstName || ''} ${lawyer.lastName || ''}`.trim();
											return (
												<button
													key={lawyer.user_no}
													type="button"
													role="option"
													aria-selected={isSelected}
													className={`change-owner-lawyer-thumb${isSelected ? ' is-selected' : ''}`}
													onClick={() => {
														setSelectedNewOwnerId(lawyer.user_no);
														prefillReassignHourlyRate(lawyer.user_no, matchLawyers);
													}}
													disabled={ownerChangeLoading}
												>
													<span className="change-owner-lawyer-thumb-rank">
														{t('project.matchRankLabel', { rank: lawyer.rank ?? '—' })}
													</span>
													<div className="change-owner-lawyer-thumb-photo">
														{lawyer.picture_url ? (
															<img src={lawyer.picture_url} alt="" />
														) : (
															<span className="change-owner-lawyer-thumb-initials">
																{getLawyerInitials(lawyer.firstName, lawyer.lastName)}
															</span>
														)}
													</div>
													<div className="change-owner-lawyer-thumb-meta">
														<span className="change-owner-lawyer-thumb-name">{name}</span>
														<span className="change-owner-lawyer-thumb-rating">
															<i className="fa-solid fa-star" aria-hidden="true" />
															{formatLawyerRating(lawyer.rating)}
														</span>
													</div>
													<p className="change-owner-lawyer-thumb-stats">
														{t('project.matchScoreLabel')}: {Number(lawyer.sigma ?? 0).toFixed(2)}
														{lawyer.hourlyRate != null && Number(lawyer.hourlyRate) > 0 && (
															<>
																{' · '}
																{t('project.lawyerHourlyRateLabel')}: {Number(lawyer.hourlyRate).toFixed(2)} €/h
																{lawyer.isBusinessRate && (
																	<> ({t('project.businessRateNote')})</>
																)}
															</>
														)}
													</p>
													<p className="change-owner-lawyer-thumb-stats">
														{t('project.matchLawyerWorkload', {
															active: lawyer.activeProjects ?? 0,
															started: lawyer.startedLast15Days ?? 0,
														})}
													</p>
												</button>
											);
										})}
									</div>
								)}
							</div>
							{selectedNewOwnerId && (
								<div className="form-row col-full row-rate-input">
									<label>{t('project.reassignHourlyRateLabel')}</label>
									<input
										type="number"
										step="0.01"
										min="0"
										placeholder={t('project.reassignHourlyRatePlaceholder')}
										value={reassignHourlyRate}
										onChange={(e) => setReassignHourlyRate(e.target.value)}
										disabled={ownerChangeLoading}
									/>
									<small>{t('project.reassignHourlyRateHelp')}</small>
								</div>
							)}
							{/* REPLY SOLUTION: to be removed from code in future. clearInitialResponse and restoreReplyTask UI removed. */}
							<div className="form-row col-full row-actions">
								<button
									className="button button-large"
									onClick={() => {
										setShowChangeOwnerModal(false);
										setSelectedNewOwnerId('');
										setMatchLawyers([]);
										setMatchCriteria(null);
										setReassignHourlyRate('');
										/* REPLY SOLUTION: to be removed from code in future.
										setClearInitialResponse(false);
										setRestoreReplyTaskIndex(null);
										*/
										setOwnerChangeFeedback([]);
									}}
									disabled={ownerChangeLoading}
								>
									{t('common.cancel')}
								</button>
								<button
									className="button button-large button-blue"
									onClick={handleChangeOwner}
									disabled={ownerChangeLoading || !selectedNewOwnerId}
								>
									{ownerChangeLoading ? t('project.changing') : t('project.changeOwner')}
								</button>
							</div>
						</>
					)}
				</div>
				</div>
			</div>
		)}

			{/* Custom Hourly Rate Modal */}
			
			{showHourlyRateModal && project && (
				<div className="modal hourly-rate" onClick={()=>setShowHourlyRateModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className = "modalclose"
							onClick   = {() => {
								setShowHourlyRateModal(false);
								setCustomHourlyRate('');
							}}
							>
								&times;
							</span>
							<h2>{t('project.setCustomHourlyRate')}</h2>
						</div>
						<div className="modal-form d-flex flex-row flex-wrap">
							<div className="form-row col-full row-text">
								<p>
									{t('project.setCustomRateFor', { number: project.project_no })}
								</p>
								<p>
									{(() => {
										// Determine which rate to show as "current rate"
										let currentRate;
										if (project.project_price !== null && project.project_price !== undefined && project.project_price > 0) {
											currentRate = { value: project.project_price, label: t('project.projectRateAtCreation') };
										} else if (owner?.partner_hourly_rate) {
											currentRate = { value: owner.partner_hourly_rate, label: t('project.defaultRateFromProfile') };
										} else {
											currentRate = { value: 0, label: t('project.noDefaultRate') };
										}
										
										return (
											<>{t('project.currentRate', { label: currentRate.label })} <strong>{Number(currentRate.value).toFixed(2)} {t('project.perHour')}</strong></>
										);
									})()}
								</p>
							</div>
							<div className="form-row col-full row-rate-input">
								<label>{t('project.customHourlyRateLabel')}</label>
								<input
									type="number"
									step="0.01"
									min="0"
									placeholder={t('project.customRatePlaceholder')}
									value={customHourlyRate}
									onChange={(e) => setCustomHourlyRate(e.target.value)}
									disabled={hourlyRateLoading}
								/>
								<small>
									{project.project_price !== null && project.project_price !== undefined && project.project_price > 0
										? t('project.leaveEmptyProjectRate')
										: t('project.leaveEmptyDefaultRate')}
								</small>
							</div>
							<div className="form-row col-full row-actions">
								<button
									className="button button-large"
									onClick={() => {
										setShowHourlyRateModal(false);
										setCustomHourlyRate('');
									}}
									disabled={hourlyRateLoading}
								>
									{t('common.cancel')}
								</button>
								<button
									className="button button-large button-blue"
									onClick={handleSetHourlyRate}
									disabled={hourlyRateLoading}
								>
									{hourlyRateLoading ? t('project.setting') : t('project.setRate')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Edit Project Modal */}
			
			{showEditProjectModal && project && (
				<div className="modal project" onClick={()=>setShowEditProjectModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className = "modalclose"
							onClick   = {() => setShowEditProjectModal(false)}
							>
								&times;
							</span>
							<h2>{t('project.editProjectTitle')}</h2>
						</div>
						<div className="modal-body" style={{maxHeight: '70vh', overflowY: 'auto'}}>
							<div className="modal-form">
								<form onSubmit={handleUpdateProject}>
									<div className="form-row col-full">
										<label>{t('project.projectName')}</label>
										<input
											type="text"
											placeholder={t('project.projectNamePlaceholder')}
											value={editProjectForm.project_name}
											onChange={e => setEditProjectForm({...editProjectForm, project_name: e.target.value})}
											required
										/>
									</div>
									<div className="form-row col-full">
										<label>{t('project.projectDescription')}</label>
										<textarea
											placeholder={t('project.projectDescriptionPlaceholder')}
											value={editProjectForm.project_description}
											onChange={e => setEditProjectForm({...editProjectForm, project_description: e.target.value})}
											required
										/>
									</div>
									{/* Project Type - Only show for admin users */}
									{currentUser && currentUser.role === 'admin' && (
									<div className="form-row col-full">
										<label>{t('project.projectType')}</label>
										<select
											value={editProjectForm.project_type}
											onChange={e => setEditProjectForm({...editProjectForm, project_type: e.target.value})}
										>
											<option value="General Booking">{t('project.typeGeneralBooking')}</option>
											<option value="personal">{t('project.typePersonalBooking')}</option>
											<option value="admin">{t('project.typeAdmin')}</option>
											<option value="lawyer">{t('project.typeLawyer')}</option>
											<option value="prospect">{t('project.typeProspect')}</option>
											<option value="hugo+">{t('project.typeHugoPlus')}</option>
											<option value="quote">{t('project.typeQuote')}</option>
										</select>
									</div>
									)}
									<div className="form-row col-full row-actions">
										<button type="button" className="button button-large" onClick={() => setShowEditProjectModal(false)}>
											{t('common.cancel')}
										</button>
										<button type="submit" className="button button-large button-blue">
											{t('common.update')}
										</button>
									</div>
								</form>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Cancel Project Modal */}
			
			{showCancelProjectModal && project && (
				<div className="modal project" onClick={()=>!isCancelling && setShowCancelProjectModal(false)}>
					<div className="modal-content" onClick={e=>e.stopPropagation()}>
						<div className="modal-header">
							<span
							className = "modalclose"
							onClick   = {() => !isCancelling && setShowCancelProjectModal(false)}
							>
								&times;
							</span>
							<h2>{t('project.declineProjectTitle')}</h2>
						</div>
						<div className="modal-form d-flex flex-row flex-wrap">
							<div className="form-row col-full row-text">
								<p>
									{t('project.declineReasonPrompt')}
								</p>
							</div>
							<form onSubmit={handleConfirmCancel} style={{width: '100%', display: 'flex', flexDirection: 'column'}}>
								<div className="form-row col-full">
									<label>{t('project.declineReason')} <span style={{color: 'red'}}>*</span></label>
									<textarea
										placeholder={t('project.declinePlaceholder')}
										value={cancelReason}
										onChange={(e) => {
											setCancelReason(e.target.value);
											if (cancelReasonError) setCancelReasonError('');
										}}
										disabled={isCancelling}
										rows={5}
										style={{
											borderColor: cancelReasonError ? '#dc3545' : undefined
										}}
									/>
									{cancelReasonError && (
										<div style={{
											color: '#dc3545',
											fontSize: '13px',
											marginTop: '5px'
										}}>
											{cancelReasonError}
										</div>
									)}
								</div>
								<div className="form-row col-full row-actions">
									<button 
										type="button" 
										className="button button-large" 
										onClick={() => setShowCancelProjectModal(false)}
										disabled={isCancelling}
									>
										{t('common.cancel')}
									</button>
									<button 
										type="submit" 
										className="button button-large button-blue"
										disabled={isCancelling}
									>
										{isCancelling ? t('project.declining') : t('project.confirm')}
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}

			<ClientEditModal
				client={client}
				open={showClientEditModal}
				onClose={() => setShowClientEditModal(false)}
				onSaved={async (updatedClient) => {
					setClient(updatedClient);
					if (project?.project_no) {
						try {
							const projectResponse = await axios.get(`/projects/${project.project_no}`);
							setProject(projectResponse);
						} catch (error) {
							logger.warn('Failed to refresh project after client save:', error);
						}
					}
				}}
				onVerified={refreshClientAndProject}
				projectId={project?._id}
				projectType={project?.project_type}
				projectNo={project?.project_no}
			/>
		</div>
	);
};

// Memoize the Project component to prevent unnecessary re-renders
export const Project = React.memo(ProjectComponent);
